import * as THREE from 'three';
import { Font } from 'three/addons/loaders/FontLoader.js';
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import Stats from 'three/addons/libs/stats.module.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { AfterimagePass } from 'three/addons/postprocessing/AfterimagePass.js';
import GUI from 'lil-gui';
import opentype from 'opentype.js';
import { Hct, TemperatureCache, SchemeContent, hexFromArgb } from '@material/material-color-utilities';
import './styles.css';
import DEFAULTS from './scene-defaults.json';

// ───────────────────────────────────────────
// CONFIG
// ───────────────────────────────────────────
const LS = { theme: 'hp_theme', lang: 'hp_lang' };

// ───────────────────────────────────────────
// TRANSLATIONS
// ───────────────────────────────────────────
const T = {
  en: {
    heroCta: 'Products',
    navProducts: 'Products',
    navContact: 'Contact',
    navAccount: 'Account',
    productsLabel: 'PRODUCTS',
    productsTitle: 'What we offer',
    prodMakeoverTitle: 'Makeover Expert',
    prodMakeoverDesc: 'Transform your outdated website into a fast, modern, mobile-friendly site. Built with AI, reviewed by a human.',
    prodMakeoverCta: 'Visit makeoverexpert.nl \u2192',
    contactLabel: 'CONTACT',
    contactTitle: 'Get in touch',
    fieldName: 'Name',
    fieldNameOpt: 'Name (optional)',
    fieldEmail: 'Email',
    fieldWebsite: 'Your current website (optional)',
    fieldMsg: 'Message',
    sendBtn: 'Send message',
    formOk: 'Message sent successfully!',
    formErr: 'Please fill in all required fields',
    verifyHuman: 'Verify you\'re human',
    footer: '\u00a9 2026 Holographic Projections',
  },
  nl: {
    heroCta: 'Producten',
    navProducts: 'Producten',
    navContact: 'Contact',
    navAccount: 'Account',
    productsLabel: 'PRODUCTEN',
    productsTitle: 'Wat wij bieden',
    prodMakeoverTitle: 'Makeover Expert',
    prodMakeoverDesc: 'Transformeer uw verouderde website naar een snelle, moderne, mobielvriendelijke site. Gebouwd met AI, gecontroleerd door een mens.',
    prodMakeoverCta: 'Bezoek makeoverexpert.nl \u2192',
    contactLabel: 'CONTACT',
    contactTitle: 'Neem contact op',
    fieldName: 'Naam',
    fieldNameOpt: 'Naam (optioneel)',
    fieldEmail: 'E-mail',
    fieldWebsite: 'Uw huidige website (optioneel)',
    fieldMsg: 'Bericht',
    sendBtn: 'Verstuur bericht',
    formOk: 'Bericht succesvol verzonden!',
    formErr: 'Vul alle verplichte velden in',
    verifyHuman: 'Bevestig dat u een mens bent',
    footer: '\u00a9 2026 Holographic Projections',
  }
};

// ───────────────────────────────────────────
// STATE
// ───────────────────────────────────────────
const $ = s => document.querySelector(s);
const $$ = s => document.querySelectorAll(s);
const isDev = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || new URLSearchParams(location.search).has('dev');
const MAGIC_SMUDGE_ANGLE = -0.15;

let lang = localStorage.getItem(LS.lang) || (navigator.language?.startsWith('nl') ? 'nl' : 'en');
let themeSetting = localStorage.getItem(LS.theme) || 'dark';
let theme = themeSetting === 'auto' ? (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark') : themeSetting;
// Listen for system theme changes when in auto mode
window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', e => {
  if (themeSetting === 'auto') applyTheme(e.matches ? 'light' : 'dark', true);
});

// ───────────────────────────────────────────
// THEME
// ───────────────────────────────────────────

function applyTheme(t, fromSystem) {
  if (!fromSystem) {
    themeSetting = t;
    localStorage.setItem(LS.theme, t);
  }
  theme = t === 'auto' ? (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark') : t;
  document.documentElement.setAttribute('data-theme', theme);
  const btn = $('#theme-toggle');
  if (btn) {
    if (themeSetting === 'auto') btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/><path d="M12 6a6 6 0 010 12V6z" fill="currentColor"/></svg>';
    else btn.innerHTML = theme === 'dark'
      ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>'
      : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>';
  }
  const tw = document.querySelector('.cf-turnstile');
  if (tw) tw.setAttribute('data-theme', theme);
  if (window._hpApplyAccent) window._hpApplyAccent();
  else if (syncFogToTheme) requestAnimationFrame(syncFogToTheme);
  // Flush canvas — needed on mobile where the render loop is paused inside overlays
  if (window._hpFlushRender) window._hpFlushRender();
}

// ───────────────────────────────────────────
// LANGUAGE
// ───────────────────────────────────────────
function applyLang(l) {
  lang = l;
  document.documentElement.lang = l;
  localStorage.setItem(LS.lang, l);
  $$('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n');
    if (T[l]?.[key] !== undefined) el.textContent = T[l][key];
  });
  const btn = $('#lang-toggle');
  if (btn) btn.textContent = l === 'en' ? 'NL' : 'EN';
}

// ───────────────────────────────────────────

// ───────────────────────────────────────────
// CONTACT FORM
// ───────────────────────────────────────────
let pendingForm = null;

// Called by Turnstile on success via data-callback
window.onTurnstileSuccess = function() {
  const overlay = $('#turnstile-overlay');
  overlay.classList.remove('active');
  if (pendingForm) submitForm(pendingForm);
  pendingForm = null;
};

function handleSubmit(e) {
  e.preventDefault();
  const form = e.target;
  if (!form.checkValidity()) { form.reportValidity(); return; }

  // If Turnstile already solved (test key auto-solves), submit directly
  const turnstileResponse = typeof turnstile !== 'undefined' ? turnstile.getResponse() : null;
  if (turnstileResponse) {
    submitForm(form);
    return;
  }

  // Turnstile not loaded or not yet solved — show overlay
  pendingForm = form;
  const overlay = $('#turnstile-overlay');
  overlay.classList.add('active');
}

async function submitForm(form) {
  const name = $('#c-name').value;
  const email = $('#c-email').value;
  const message = $('#c-msg').value;
  const turnstileToken = typeof turnstile !== 'undefined' ? turnstile.getResponse() : '';

  // Disable button while sending
  const btn = form.querySelector('.btn-submit');
  const origText = btn.textContent;
  btn.disabled = true;
  btn.textContent = '...';

  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, message, turnstileToken }),
    });
    const data = await res.json();

    if (res.ok && data.success) {
      $('#form-error').classList.add('hidden');
      $('#form-success').classList.remove('hidden');
      form.reset();
      if (typeof turnstile !== 'undefined') turnstile.reset();
      setTimeout(() => $('#form-success').classList.add('hidden'), 6000);
    } else {
      $('#form-error').textContent = data.error || 'Something went wrong';
      $('#form-error').classList.remove('hidden');
    }
  } catch (err) {
    $('#form-error').textContent = 'Network error, please try again';
    $('#form-error').classList.remove('hidden');
  } finally {
    btn.disabled = false;
    btn.textContent = origText;
  }
}

// ───────────────────────────────────────────
// SCROLL REVEAL
// ───────────────────────────────────────────
function initReveals() {
  const obs = new IntersectionObserver(entries => {
    entries.forEach(en => { if (en.isIntersecting) en.target.classList.add('revealed'); });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
  $$('.reveal').forEach(el => obs.observe(el));
}

// ───────────────────────────────────────────
// NAV
// ───────────────────────────────────────────
const navFlyTargets = { '#home': 'home', '#products': 'products', '#contact': 'contact' };

function initNav() {
  $$('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      const href = a.getAttribute('href');
      const target = navFlyTargets[href];
      if (target) {
        flyTo(target);
        history.replaceState(null, '', href);
      }
    });
  });
  // Fly to hash on page load
  const hash = location.hash || '#home';
  const target = navFlyTargets[hash];
  if (target && target !== 'home') {
    // Defer so scene is ready
    requestAnimationFrame(() => flyTo(target));
  }
}

// ───────────────────────────────────────────
// THREE.JS  —  WEBGPU OCEAN + ICE TEXT
// ───────────────────────────────────────────
const CURVE_SUBDIVISIONS = 12; // pre-tessellate curves into line segments

function otToThreeFont(font) {
  const r = v => Math.round(v * 10) / 10;
  const scale = 100000 / ((font.unitsPerEm || 2048) * 10);
  const glyphs = {};
  const map = font.encoding?.cmap?.glyphIndexMap;
  if (!map) throw new Error('cmap missing');

  for (const unicode of Object.keys(map)) {
    const glyph = font.glyphs.get(map[unicode]);
    const tok = { ha: r(glyph.advanceWidth * scale), o: '' };
    if (glyph.path) {
      let cx = 0, cy = 0; // current position
      for (const c of glyph.path.commands) {
        if (c.type === 'M') {
          tok.o += `m ${r(c.x*scale)} ${r(c.y*scale)} `;
          cx = c.x; cy = c.y;
        } else if (c.type === 'L') {
          tok.o += `l ${r(c.x*scale)} ${r(c.y*scale)} `;
          cx = c.x; cy = c.y;
        } else if (c.type === 'Q') {
          // Pre-subdivide quadratic bezier into line segments
          for (let i = 1; i <= CURVE_SUBDIVISIONS; i++) {
            const t = i / CURVE_SUBDIVISIONS;
            const mt = 1 - t;
            const x = mt*mt*cx + 2*mt*t*c.x1 + t*t*c.x;
            const y = mt*mt*cy + 2*mt*t*c.y1 + t*t*c.y;
            tok.o += `l ${r(x*scale)} ${r(y*scale)} `;
          }
          cx = c.x; cy = c.y;
        } else if (c.type === 'C') {
          // Pre-subdivide cubic bezier into line segments
          for (let i = 1; i <= CURVE_SUBDIVISIONS; i++) {
            const t = i / CURVE_SUBDIVISIONS;
            const mt = 1 - t;
            const x = mt*mt*mt*cx + 3*mt*mt*t*c.x1 + 3*mt*t*t*c.x2 + t*t*t*c.x;
            const y = mt*mt*mt*cy + 3*mt*mt*t*c.y1 + 3*mt*t*t*c.y2 + t*t*t*c.y;
            tok.o += `l ${r(x*scale)} ${r(y*scale)} `;
          }
          cx = c.x; cy = c.y;
        }
      }
    }
    glyphs[String.fromCharCode(unicode)] = tok;
  }
  return {
    glyphs,
    familyName: font.getEnglishName('fontFamily'),
    ascender: r(font.ascender * scale),
    descender: r(font.descender * scale),
    underlinePosition: font.tables.post.underlinePosition,
    underlineThickness: font.tables.post.underlineThickness,
    boundingBox: {
      xMin: font.tables.head.xMin, xMax: font.tables.head.xMax,
      yMin: font.tables.head.yMin, yMax: font.tables.head.yMax
    },
    resolution: 1000,
    original_font_information: font.tables.name
  };
}

// ───────────────────────────────────────────
// THREE.JS  —  FONT LOADER
// ───────────────────────────────────────────
async function loadThreeFont(fontName) {
  const name = fontName || 'Syne';
  const slug = name.toLowerCase().replace(/\s+/g, '-');
  if (typeof opentype !== 'undefined') {
    // Try multiple fontsource URL patterns
    const urls = [
      `https://cdn.jsdelivr.net/npm/@fontsource/${slug}/files/${slug}-latin-800-normal.woff`,
      `https://cdn.jsdelivr.net/npm/@fontsource/${slug}/files/${slug}-latin-700-normal.woff`,
      `https://cdn.jsdelivr.net/npm/@fontsource/${slug}/files/${slug}-latin-400-normal.woff`,
      `https://cdn.jsdelivr.net/npm/@fontsource/${slug}/files/${slug}-all-800-normal.woff`,
      `https://cdn.jsdelivr.net/npm/@fontsource/${slug}/files/${slug}-all-700-normal.woff`,
      `https://cdn.jsdelivr.net/npm/@fontsource/${slug}/files/${slug}-all-400-normal.woff`,
    ];
    for (const url of urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const buf = await res.arrayBuffer();
        const ot = opentype.parse(buf);
        return new Font(otToThreeFont(ot));
      } catch (_) { /* try next */ }
    }
    console.warn(`Could not load "${name}" from fontsource, using fallback`);
  }
  const res = await fetch('https://cdn.jsdelivr.net/npm/three@0.183.2/examples/fonts/helvetiker_bold.typeface.json');
  return new Font(await res.json());
}

// ───────────────────────────────────────────
// THREE.JS  —  WEBGPU SCENE
// ───────────────────────────────────────────
let renderer, scene, camera, textGroup, responsiveWrapper, stats, camLight, material;
let composer, bloomPass, smaaPass, afterimagePass;
const wordOpacity = { holo: 1, proj: 1 };
const cameraConfig = { type: (DEFAULTS.camera || DEFAULTS.cam)?.type || 'perspective', orthoZoom: (DEFAULTS.camera || DEFAULTS.cam)?.orthoZoom || 3 };
const timeUniform = { value: 0 };
const animParams = { ...(DEFAULTS.anim || DEFAULTS.animation || { letterInterval: 0, fadeTime: 300 }) };
const instanceParams = {
  holo: { count: 1, posSpread: { x: 0, y: 0, z: 0 }, rotSpread: { x: 0, y: 0, z: 0 }, opacityMin: 1, opacityMax: 1 },
  proj: { count: 1, posSpread: { x: 0, y: 0, z: 0 }, rotSpread: { x: 0, y: 0, z: 0 }, opacityMin: 1, opacityMax: 1 },
};
const instancedMeshes = []; // track for cleanup
const _compileQueue = []; // staggered shader compilation
const mobileParams = JSON.parse(JSON.stringify(DEFAULTS.mobile));
if (!mobileParams.holoScale) mobileParams.holoScale = { x: 1, y: 1, z: 1 };
if (!mobileParams.projScale) mobileParams.projScale = { x: 1, y: 1, z: 1 };

function applyMobileOverrides() {
  if (!textGroup || textGroup.children.length < 2) return;
  const isMobile = window.innerWidth < mobileParams.breakpoint;
  const holo = textGroup.children[0];
  const proj = textGroup.children[1];
  if (isMobile && mobileParams.enabled) {
    if (holo._desktopPos === undefined) {
      holo._desktopPos = holo.position.clone();
      proj._desktopPos = proj.position.clone();
      holo._desktopScale = holo.scale.clone();
      proj._desktopScale = proj.scale.clone();
    }
    holo.position.set(mobileParams.holoPos.x, mobileParams.holoPos.y, mobileParams.holoPos.z);
    proj.position.set(mobileParams.projPos.x, mobileParams.projPos.y, mobileParams.projPos.z);
    const hs = mobileParams.holoScale || { x: 1, y: 1, z: 1 };
    const ps = mobileParams.projScale || { x: 1, y: 1, z: 1 };
    holo.scale.set(holo._desktopScale.x * hs.x, holo._desktopScale.y * hs.y, holo._desktopScale.z * hs.z);
    proj.scale.set(proj._desktopScale.x * ps.x, proj._desktopScale.y * ps.y, proj._desktopScale.z * ps.z);
  } else if (holo._desktopPos) {
    holo.position.copy(holo._desktopPos);
    proj.position.copy(proj._desktopPos);
    holo.scale.copy(holo._desktopScale);
    proj.scale.copy(proj._desktopScale);
    holo._desktopPos = undefined;
    proj._desktopPos = undefined;
    holo._desktopScale = undefined;
    proj._desktopScale = undefined;
  }
}

// Camera fly system — 3 poses as offsets from their targets
const _camDef = DEFAULTS.camera || DEFAULTS.cam || {};
const camPoses = JSON.parse(JSON.stringify(_camDef.poses || { home: { x: 0, y: 200, z: 200, rx: -0.8, ry: 0, rz: 0, fov: 70, oz: 3 }, products: { x: 0, y: 0, z: 532, rx: 0, ry: 0, rz: 0, fov: 90, oz: 3 }, contact: { x: 0, y: 0, z: 532, rx: 0, ry: 0, rz: 0, fov: 90, oz: 3 } }));
const flyConfig = { duration: _camDef.flyDuration || 0.4, enabled: _camDef.flyEnabled !== false };
let flyAnim = null; // { from, to, startTime, duration }
let currentPose = 'home';

// Pro easing: custom curve with slight overshoot then settle
function proEase(t) {
  // Combination: fast ease-in, smooth ease-out with micro-overshoot
  if (t < 0.0) return 0;
  if (t > 1.0) return 1;
  // Modified quintic ease-in-out with overshoot
  const p = t < 0.5
    ? 16 * t * t * t * t * t
    : 1 + 16 * Math.pow(t - 1, 5);
  // Add subtle overshoot at 80-95% mark
  const overshoot = Math.sin(t * Math.PI) * 0.02 * (1 - t);
  return Math.max(0, Math.min(1.02, p + overshoot));
}

function resolvePose(name) {
  const pose = camPoses[name];
  return {
    x: pose.x, y: pose.y, z: pose.z,
    rx: pose.rx, ry: pose.ry, rz: pose.rz,
    fov: pose.fov, oz: pose.oz,
  };
}

let _iframeTimer = null;
function updateProductsIframe(poseName) {
  clearTimeout(_iframeTimer);
  const container = document.querySelector('.phone-screen');
  if (!container) return;
  const iframe = container.querySelector('iframe');
  if (poseName === 'products') {
    if (!iframe) {
      _iframeTimer = setTimeout(() => {
        if (currentPose !== 'products') return;
        // Show spinner while iframe loads
        const spinner = document.createElement('div');
        spinner.className = 'phone-spinner';
        spinner.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;';
        spinner.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="38" stroke-linecap="round" opacity="0.5"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></circle></svg>';
        container.appendChild(spinner);
        const el = document.createElement('iframe');
        el.src = 'https://makeoverexpert.nl?embed=phone';
        // Size iframe to exact container dimensions, scale from 375px mobile viewport
        const w = container.offsetWidth;
        const h = container.offsetHeight;
        const scale = w / 375;
        el.style.cssText = `width:375px;height:${Math.ceil(h / scale)}px;border:none;transform:scale(${scale});transform-origin:0 0;opacity:0;transition:opacity 0.8s;`;
        el.loading = 'lazy';
        el.onload = () => { spinner.remove(); requestAnimationFrame(() => { el.style.opacity = '1'; }); };
        container.appendChild(el);
      }, flyConfig.duration * 1000);
    }
  } else {
    // Clean up iframes from both desktop and mobile phone frames
    if (container) {
      container.querySelector('.phone-spinner')?.remove();
      const existing = container.querySelector('iframe');
      if (existing) {
        existing.style.opacity = '0';
        setTimeout(() => existing.remove(), 800);
      }
    }
  }
}

let _overlayHideTimer = null;
function hideOverlays(exceptId) {
  clearTimeout(_overlayHideTimer);
  const overlays = [...document.querySelectorAll('.overlay.active')].filter(el => el.id !== exceptId);
  overlays.forEach(el => { el.style.opacity = '0'; });
  if (overlays.length) {
    _overlayHideTimer = setTimeout(() => {
      overlays.forEach(el => el.classList.remove('active'));
    }, 300);
  }
}

let _lastOverlayOpenTime = 0;

function flyTo(poseName) {
  updateProductsIframe(poseName);

  // Show/hide overlay divs for products and contact
  const isMobile = window.innerWidth < (mobileParams?.breakpoint || 768);
  if (poseName === 'products' || poseName === 'contact') {
    const overlayId = 'overlay-' + poseName;
    hideOverlays(overlayId);
    const overlay = document.getElementById(overlayId);
    if (overlay) {
      overlay.classList.add('active');
      requestAnimationFrame(() => { overlay.style.opacity = '1'; });
      _lastOverlayOpenTime = performance.now();
    }
    if (isMobile && window._hpPauseRender) window._hpPauseRender(true);
  } else {
    hideOverlays();
    if (isMobile && window._hpPauseRender) window._hpPauseRender(false);
  }

  if (!flyConfig.enabled) { currentPose = poseName; return; }
  const to = resolvePose(poseName);
  flyAnim = {
    from: {
      x: camera.position.x, y: camera.position.y, z: camera.position.z,
      rx: camera.rotation.x, ry: camera.rotation.y, rz: camera.rotation.z,
      fov: camera.fov || 90, oz: cameraConfig.orthoZoom,
    },
    to,
    startTime: performance.now(),
    duration: flyConfig.duration * 1000,
  };
  currentPose = poseName;
}
const noiseParams = { ...DEFAULTS.noise };

// Simplex noise GLSL (compact 3D)
const noiseGLSL = `
  vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec4 perm(vec4 x){return mod289(((x*34.0)+1.0)*x);}
  float snoise(vec3 p){
    vec3 a=floor(p);vec3 d=p-a;d=d*d*(3.0-2.0*d);
    vec4 b=a.xxyy+vec4(0.0,1.0,0.0,1.0);
    vec4 k1=perm(b.xyxy);vec4 k2=perm(k1.xyxy+b.zzww);
    vec4 c=k2+a.zzzz;vec4 k3=perm(c);vec4 k4=perm(c+1.0);
    vec4 o1=fract(k3*(1.0/41.0));vec4 o2=fract(k4*(1.0/41.0));
    vec4 o3=o2*d.z+o1*(1.0-d.z);vec2 o4=o3.yw*d.x+o3.xz*(1.0-d.x);
    return o4.y*d.y+o4.x*(1.0-d.y);
  }
`;

function applyNoiseShader(mat) {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = timeUniform;
    shader.uniforms.uNoiseSpeed = { value: noiseParams.speed };
    shader.uniforms.uNoiseScale = { value: noiseParams.scale };
    shader.uniforms.uNoiseIntensity = { value: noiseParams.intensity };
    // Use vertex position as noise input (works on all geometry, no UV dependency issues)
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      `#include <common>
      varying vec3 vWorldPos;`
    );
    shader.vertexShader = shader.vertexShader.replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
      vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
      uniform float uTime;
      uniform float uNoiseSpeed;
      uniform float uNoiseScale;
      uniform float uNoiseIntensity;
      varying vec3 vWorldPos;
      ${noiseGLSL}`
    );
    // Animate normal map UVs with noise — makes the ice texture flow like lava
    // Procedural normal perturbation from noise derivatives
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <normal_fragment_maps>',
      `{
        vec3 np = vWorldPos * uNoiseScale * 0.5 + vec3(uTime * uNoiseSpeed * 0.3);
        float eps = 0.01;
        float n0 = snoise(np);
        float nx = snoise(np + vec3(eps, 0.0, 0.0));
        float ny = snoise(np + vec3(0.0, eps, 0.0));
        float nz = snoise(np + vec3(0.0, 0.0, eps));
        vec3 grad = vec3(nx - n0, ny - n0, nz - n0) / eps;
        normal = normalize(normal - grad * uNoiseIntensity);
      }`
    );
    mat.userData.shader = shader;
  };
  mat.needsUpdate = true;
}
const fadingLetters = [];
const primaryColor3D = new THREE.Color().setHSL(200/360, 0.8, 0.6, THREE.SRGBColorSpace);
const tertiaryColor3D = new THREE.Color().setHSL(260/360, 0.6, 0.5, THREE.SRGBColorSpace);
const secondLaserParams = { thicknessMin: 1.0, thicknessMax: 2.0, ...(DEFAULTS.secondLaser || DEFAULTS.horizonLaser || {}) };
const thirdLaserParams = { thicknessMin: 1.0, thicknessMax: 2.0, ...(DEFAULTS.thirdLaser || {}) };
let secondBeams = [];
let thirdBeams = [];
let thirdBuilt = false;
let secondBuilt = false;
const laserGroup = new THREE.Group();
let syncFogToTheme = null;

function getLaserColor(colorOverride) {
  const src = colorOverride || 'primary';
  if (src === 'primary') return primaryColor3D.clone();
  if (src === 'tertiary') return tertiaryColor3D.clone();
  if (src === 'primary-tertiary') return new THREE.Color().copy(primaryColor3D).lerp(tertiaryColor3D, Math.random());
  if (src === 'tertiary-primary') return new THREE.Color().copy(tertiaryColor3D).lerp(primaryColor3D, Math.random());
  if (src === 'complementary') { const c = primaryColor3D.clone(); const hsl = {}; c.getHSL(hsl, THREE.SRGBColorSpace); c.setHSL((hsl.h + 0.5) % 1, hsl.s, hsl.l, THREE.SRGBColorSpace); return c; }
  if (src === 'analogous') { const c = primaryColor3D.clone(); const hsl = {}; c.getHSL(hsl, THREE.SRGBColorSpace); c.setHSL((hsl.h + (Math.random() * 0.15 - 0.075)) % 1, hsl.s, hsl.l, THREE.SRGBColorSpace); return c; }
  if (src === 'white') return new THREE.Color(0xffffff);
  return primaryColor3D.clone();
}
let targetRotX = 0, targetRotY = 0;

// Mouse X → hue mapping with LPF
const mouseMapping = { enabled: false, hueMin: 0, hueMax: 360, smoothing: 0.05 };
let mouseRawX = 0.5, mouseRawY = 0.5, mouseSmoothedX = 0.5, _mouseHasMoved = false;

// LFO system — oscillators that drive parameters
const lfos = [];
const WAVEFORMS = ['sine', 'triangle', 'square', 'saw', 'random', 'mouseX', 'mouseY', 'holoLaser', 'projLaser'];
let holoLaserStrength = 0, projLaserStrength = 0;

function createLFO({ name, target, targets, prop, min, max, freq, waveform, phase, enabled, smoothing, strength, power, timeSource }) {
	return { name: name || `${target}`, target, targets: targets || [], prop, min: min ?? 0, max: max ?? 1, freq: freq ?? 0.5, waveform: waveform ?? 'sine', phase: phase ?? 0, enabled: enabled ?? true, smoothing: smoothing ?? 50, strength: strength ?? 1, power: power ?? 1, timeSource: timeSource ?? 'global', _smoothed: 0.5, _rampStart: -1 };
}
function lfoValue(lfo, t) {
	// Pick time source
	if (lfo.timeSource === 'laser') t = laserTime;
	else if (lfo.timeSource === 'text') t = textTime;
	let v;
	switch (lfo.waveform) {
		case 'mouseX': v = mouseRawX; break;
		case 'mouseY': v = 1 - (typeof mouseRawY !== 'undefined' ? mouseRawY : 0.5); break;
		case 'holoLaser': v = holoLaserStrength; break;
		case 'projLaser': v = projLaserStrength; break;
		case 'sine': { const p = t * lfo.freq * Math.PI * 2 + lfo.phase; v = (Math.sin(p) + 1) / 2; break; }
		case 'triangle': { const p = t * lfo.freq * Math.PI * 2 + lfo.phase; v = Math.abs(((p / Math.PI) % 2) - 1); break; }
		case 'square': { const p = t * lfo.freq * Math.PI * 2 + lfo.phase; v = Math.sin(p) > 0 ? 1 : 0; break; }
		case 'saw': { const p = t * lfo.freq * Math.PI * 2 + lfo.phase; v = ((p / (Math.PI * 2)) % 1 + 1) % 1; break; }
		case 'random': {
			// Continuous noise using layered sine waves (cheap pseudo-Perlin)
			const rt = t * lfo.freq + lfo.phase;
			v = (Math.sin(rt * 1.7 + 0.3) * 0.5 + Math.sin(rt * 3.1 + 1.7) * 0.25 + Math.sin(rt * 7.3 + 4.1) * 0.125 + Math.sin(rt * 13.7 + 2.3) * 0.0625) / 0.9375 * 0.5 + 0.5;
			break;
		}
		default: { const p = t * lfo.freq * Math.PI * 2 + lfo.phase; v = (Math.sin(p) + 1) / 2; }
	}
	// Apply LPF smoothing for noisy/external sources.
	// smoothing is 0-100 GUI value, mapped exponentially: 0=frozen, 100=instant.
	// Framerate-independent: time constant tau is derived from the old 60fps-reference
	// alpha formula so saved smoothing values behave as they did at 60fps, but are
	// stable across frame drops, mobile every-3rd-frame throttling, etc.
	if (lfo.waveform === 'mouseX' || lfo.waveform === 'mouseY' || lfo.waveform === 'holoLaser' || lfo.waveform === 'projLaser' || lfo.waveform === 'random') {
		let effectiveSmoothing = lfo.smoothing;
		const rampTime = (animParams.smoothingRamp ?? 3000) / 1000;
		if (rampTime > 0 && lfo._rampStart > 0) {
			const elapsed = performance.now() / 1000 - lfo._rampStart;
			if (elapsed < rampTime) {
				const rampT = elapsed / rampTime;
				effectiveSmoothing = rampT * lfo.smoothing; // 0 → configured
			}
		}
		const nowT = performance.now() / 1000;
		// Clamp dt to 1s so a long tab-pause doesn't cause a one-shot huge jump.
		const lpfDt = lfo._lastLpfT ? Math.min(1, nowT - lfo._lastLpfT) : 1 / 60;
		lfo._lastLpfT = nowT;
		const s3 = Math.pow(Math.max(0, Math.min(100, effectiveSmoothing)) / 100, 3);
		if (s3 >= 0.9999) {
			lfo._smoothed = v; // instant
		} else if (s3 > 0) {
			const tau = -(1 / 60) / Math.log(1 - s3); // seconds, inverse of old per-frame alpha at 60fps
			const alpha = 1 - Math.exp(-lpfDt / tau);
			lfo._smoothed += (v - lfo._smoothed) * alpha;
		}
		// s3 === 0 → frozen, don't touch _smoothed
		lfo._smoothed = Math.max(0, Math.min(1, lfo._smoothed));
		v = lfo._smoothed;
	}
	if (lfo.power !== 1) v = Math.pow(v, lfo.power);
	return lfo.min + (lfo.max - lfo.min) * v * lfo.strength;
}
// LFO targets — map string keys to { get, set } accessors (populated after initThree)
const lfoTargets = {};
const fnGlobal = { enabled: true };
const timeParams = { scale: 1.0, laserScale: 1.0, textScale: 1.0 };
let scaledTime = 0, laserTime = 0, textTime = 0, lastRealTime = 0;

// Merged-word instancing — one InstancedMesh per word
const _mergedMatCache = new Map();

function rebuildInstances(wordGroup, params) {
  const worldRot = wordGroup.children[0];
  if (!worldRot) return;
  const letterMeshes = worldRot.children.filter(c => c.isMesh && !c.isInstancedMesh);
  if (!letterMeshes.length) return;
  const count = Math.max(1, params.count);

  const existing = instancedMeshes.find(im => im._wordGroup === wordGroup);

  // Generate or reuse seeded random directions (stable across calls)
  if (!params._seeds || params._seeds.length !== count) {
    params._seeds = [];
    for (let i = 0; i < count; i++) {
      params._seeds.push({
        px: Math.random() * 2 - 1, py: Math.random() * 2 - 1, pz: Math.random() * 2 - 1,
        rx: Math.random() * 2 - 1, ry: Math.random() * 2 - 1, rz: Math.random() * 2 - 1,
      });
    }
  }

  function updateMatricesAndOpacities(im) {
    const opacities = im.geometry.getAttribute('instanceOpacity');
    const dummy = new THREE.Object3D();
    for (let i = 0; i < count; i++) {
      if (i === 0) { dummy.position.set(0,0,0); dummy.rotation.set(0,0,0); }
      else {
        const s = params._seeds[i];
        dummy.position.set(s.px * params.posSpread.x, s.py * params.posSpread.y, s.pz * params.posSpread.z);
        dummy.rotation.set(s.rx * params.rotSpread.x, s.ry * params.rotSpread.y, s.rz * params.rotSpread.z);
      }
      dummy.scale.set(1,1,1); dummy.updateMatrix(); im.setMatrixAt(i, dummy.matrix);
      const t = count > 1 ? (i / (count - 1)) : 0;
      if (opacities) opacities.array[i] = i === 0 ? 1 : params.opacityMin + (params.opacityMax - params.opacityMin) * t;
    }
    if (opacities) opacities.needsUpdate = true;
    im.instanceMatrix.needsUpdate = true;
  }

  // Fast path — same count AND already merged (has _merged flag), just update matrices + opacities
  if (existing && existing.count === count && existing._merged) {
    updateMatricesAndOpacities(existing);
    return;
  }

  // Remove old
  for (let i = instancedMeshes.length - 1; i >= 0; i--) {
    if (instancedMeshes[i]._wordGroup === wordGroup) {
      instancedMeshes[i].parent?.remove(instancedMeshes[i]);
      instancedMeshes[i].dispose();
      instancedMeshes.splice(i, 1);
    }
  }

  if (count <= 1) {
    letterMeshes.forEach(m => { m.visible = true; });
    return;
  }

  // Manually merge letter geometries — concatenate position/normal buffers, reindex
  let totalVerts = 0, totalIdx = 0;
  const parts = [];
  for (const m of letterMeshes) {
    m.updateMatrix();
    const g = m.geometry;
    const pos = g.attributes.position;
    const norm = g.attributes.normal;
    const idx = g.index;
    parts.push({ pos, norm, idx, matrix: m.matrix.clone(), vertOffset: totalVerts, idxCount: idx ? idx.count : pos.count });
    totalVerts += pos.count;
    totalIdx += idx ? idx.count : pos.count;
  }
  const mergedPos = new Float32Array(totalVerts * 3);
  const mergedNorm = new Float32Array(totalVerts * 3);
  const mergedIdx = new Uint32Array(totalIdx);
  let vOff = 0, iOff = 0;
  const _v = new THREE.Vector3();
  const _n = new THREE.Vector3();
  const normalMatrix = new THREE.Matrix3();
  for (const p of parts) {
    normalMatrix.getNormalMatrix(p.matrix);
    for (let i = 0; i < p.pos.count; i++) {
      _v.fromBufferAttribute(p.pos, i).applyMatrix4(p.matrix);
      mergedPos[(vOff + i) * 3] = _v.x;
      mergedPos[(vOff + i) * 3 + 1] = _v.y;
      mergedPos[(vOff + i) * 3 + 2] = _v.z;
      _n.fromBufferAttribute(p.norm, i).applyMatrix3(normalMatrix).normalize();
      mergedNorm[(vOff + i) * 3] = _n.x;
      mergedNorm[(vOff + i) * 3 + 1] = _n.y;
      mergedNorm[(vOff + i) * 3 + 2] = _n.z;
    }
    if (p.idx) {
      for (let i = 0; i < p.idx.count; i++) mergedIdx[iOff + i] = p.idx.getX(i) + vOff;
    } else {
      for (let i = 0; i < p.pos.count; i++) mergedIdx[iOff + i] = vOff + i;
    }
    vOff += p.pos.count;
    iOff += p.idxCount;
  }
  const mergedGeo = new THREE.BufferGeometry();
  mergedGeo.setAttribute('position', new THREE.BufferAttribute(mergedPos, 3));
  mergedGeo.setAttribute('normal', new THREE.BufferAttribute(mergedNorm, 3));
  mergedGeo.setIndex(new THREE.BufferAttribute(mergedIdx, 1));

  // Get or create material (one per word, cached)
  const cacheKey = wordGroup.uuid;
  let mat = _mergedMatCache.get(cacheKey);
  if (!mat) {
    mat = letterMeshes[0].material.clone();
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = timeUniform;
      shader.uniforms.uNoiseSpeed = { value: noiseParams.speed };
      shader.uniforms.uNoiseScale = { value: noiseParams.scale };
      shader.uniforms.uNoiseIntensity = { value: noiseParams.intensity };
      shader.vertexShader = shader.vertexShader.replace('#include <common>',
        `#include <common>\nattribute float instanceOpacity;\nvarying float vInstanceOpacity;\nvarying vec3 vWorldPos;`);
      shader.vertexShader = shader.vertexShader.replace('#include <worldpos_vertex>',
        `#include <worldpos_vertex>\nvWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`);
      shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>',
        `#include <begin_vertex>\nvInstanceOpacity = instanceOpacity;`);
      shader.fragmentShader = shader.fragmentShader.replace('#include <common>',
        `#include <common>\nuniform float uTime;\nuniform float uNoiseSpeed;\nuniform float uNoiseScale;\nuniform float uNoiseIntensity;\nvarying float vInstanceOpacity;\nvarying vec3 vWorldPos;\n${noiseGLSL}`);
      shader.fragmentShader = shader.fragmentShader.replace('#include <normal_fragment_maps>',
        `{ vec3 np=vWorldPos*uNoiseScale*0.5+vec3(uTime*uNoiseSpeed*0.3); float eps=0.01; float n0=snoise(np); float nx=snoise(np+vec3(eps,0.0,0.0)); float ny=snoise(np+vec3(0.0,eps,0.0)); float nz=snoise(np+vec3(0.0,0.0,eps)); vec3 grad=vec3(nx-n0,ny-n0,nz-n0)/eps; normal=normalize(normal-grad*uNoiseIntensity); }`);
      shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>',
        `#include <color_fragment>\ndiffuseColor.a *= vInstanceOpacity;`);
      mat.userData.shader = shader;
    };
    mat.transparent = true;
    _mergedMatCache.set(cacheKey, mat);
  }

  const im = new THREE.InstancedMesh(mergedGeo, mat, count);
  im._wordGroup = wordGroup;
  im._merged = true;
  im.castShadow = true;
  im.geometry.setAttribute('instanceOpacity', new THREE.InstancedBufferAttribute(new Float32Array(count), 1));
  updateMatricesAndOpacities(im);

  im.visible = false;
  im._pendingLetters = letterMeshes;
  worldRot.add(im);
  instancedMeshes.push(im);

  if (renderer.compileAsync) {
    renderer.compileAsync(im, camera, scene).then(() => {
      // Sync color to current accent right before showing
      const isHolo = im._wordGroup === textGroup?.children[0];
      im.material.color.copy(isHolo ? primaryColor3D : tertiaryColor3D);
      if (im._pendingLetters) { im._pendingLetters.forEach(m => { m.visible = false; }); im._pendingLetters = null; }
      im.visible = true;
    });
  } else {
    _compileQueue.push(im);
  }
}

function resolveFnTarget(key) {
  const parts = key.split(':');
  if (parts.length !== 3 || parts[0] !== 'fn') return null;
  const label = parts[1];
  const param = parts[2];
  const idx = lfos.findIndex(l => l.name === label);
  if (idx < 0) return null;
  return { idx, param };
}
const lfoGuiControllers = {}; // map target name → lil-gui controller to disable when LFO active

function updateLfoDisabledState() {
  const activeTargets = new Set(lfos.filter(l => l.enabled).map(l => l.target));
  for (const [name, ctrl] of Object.entries(lfoGuiControllers)) {
    if (ctrl && ctrl.domElement) {
      ctrl.domElement.style.opacity = activeTargets.has(name) ? '0.3' : '1';
      ctrl.domElement.style.pointerEvents = activeTargets.has(name) ? 'none' : 'auto';
    }
  }
}

function initThree() {
  const container = $('#three-container');
  const w = window.innerWidth, h = window.innerHeight;

  // Renderer — WebGL
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  const _mobileDPR = DEFAULTS.mobile?.dpr ?? 1.5;
  renderer.setPixelRatio(w < (DEFAULTS.mobile?.breakpoint || 768) ? Math.min(window.devicePixelRatio, _mobileDPR) : window.devicePixelRatio);
  renderer.setSize(w, h);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  container.appendChild(renderer.domElement);

  // Bloom composer created after scene + camera below

  // Scene
  scene = new THREE.Scene();

  // Camera — supports perspective and orthographic
  const perspCam = new THREE.PerspectiveCamera(90, w / h, 1, 20000);
  perspCam.position.set(0, 30, 182.3);
  const orthoCam = new THREE.OrthographicCamera(-w / cameraConfig.orthoZoom, w / cameraConfig.orthoZoom, h / cameraConfig.orthoZoom, -h / cameraConfig.orthoZoom, 1, 20000);
  orthoCam.position.set(0, 30, 182.3);
  camera = cameraConfig.type === 'orthographic' ? orthoCam : perspCam;

  function switchCamera(type) {
    cameraConfig.type = type;
    const pos = camera.position.clone();
    const rot = camera.rotation.clone();
    if (type === 'orthographic') {
      camera = orthoCam;
    } else {
      camera = perspCam;
    }
    camera.position.copy(pos);
    camera.rotation.copy(rot);
    camera.updateProjectionMatrix();
    // Update composer render pass camera
    if (composer) composer.passes.forEach(p => { if (p.camera) p.camera = camera; });
  }

  // Fog — color synced from M3 surface-dim token
  const _fogDef = DEFAULTS.fog || {};
  scene.fog = new THREE.FogExp2(parseInt((_fogDef.color || '#131317').replace('#',''), 16), _fogDef.density || 0.0012);
  scene.fog.userData = { baseDensity: 0.0012 };
  scene.background = new THREE.Color(0x0f0d11); // opaque — updated in applyAccent() on theme change
  syncFogToTheme = function() {
    const surfaceDim = getComputedStyle(document.documentElement).getPropertyValue('--surface-dim').trim();
    if (surfaceDim && scene.fog) {
      scene.fog.color.setStyle(surfaceDim);
      scene.fog.userData.baseDensity = scene.fog.density;
    }
    // Sync initial scene background to the CSS page bg so there's no flash before applyAccent runs.
    const pageBg = getComputedStyle(document.documentElement).getPropertyValue('--page-bg').trim();
    if (pageBg && scene.background && scene.background.isColor) scene.background.setStyle(pageBg);
  };
  syncFogToTheme();

  // Post-processing — bloom for laser glow
  composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const _bloomDef = DEFAULTS.bloom || {};
  bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), _bloomDef.strength ?? 0.57, _bloomDef.radius ?? 0.13, _bloomDef.threshold ?? 0.5);
  const _isMobileInit = w < (DEFAULTS.mobile?.breakpoint || 768);
  bloomPass.enabled = _isMobileInit ? (_bloomDef.mobileBloom !== false) : true;
  composer.addPass(bloomPass);
  afterimagePass = new AfterimagePass(_bloomDef.afterimage ?? 0.93);
  afterimagePass.enabled = _isMobileInit ? (_bloomDef.mobileAfterimage !== false) : true;
  composer.addPass(afterimagePass);
  composer.addPass(new OutputPass());
  smaaPass = new SMAAPass(w * renderer.getPixelRatio(), h * renderer.getPixelRatio());
  smaaPass.enabled = _isMobileInit ? false : (_bloomDef.smaa !== false);
  composer.addPass(smaaPass);

  // Lights
  const dirLight = new THREE.DirectionalLight(0xffffff, 3);
  dirLight.position.set(50, 80, 60);
  scene.add(dirLight);
  scene.add(new THREE.AmbientLight(0x8090a0, 2));
  scene.add(laserGroup);
  camLight = new THREE.PointLight(0xffffff, 3, 200);
  scene.add(camLight);

  // Shadow mapping
  dirLight.castShadow = true;
  dirLight.shadow.mapSize.width = 2048;
  dirLight.shadow.mapSize.height = 2048;
  dirLight.shadow.camera.near = 1;
  dirLight.shadow.camera.far = 500;
  dirLight.shadow.camera.left = -200;
  dirLight.shadow.camera.right = 200;
  dirLight.shadow.camera.top = 200;
  dirLight.shadow.camera.bottom = -200;
  dirLight.shadow.bias = -0.001;
  dirLight.shadow.radius = 4;
  renderer.shadowMap.enabled = w < (DEFAULTS.mobile?.breakpoint || 768) ? (DEFAULTS.mobile?.shadows !== false) : true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  // Shadow-receiving ground plane
  const shadowMat = new THREE.ShadowMaterial({ opacity: 0.3, color: 0x000000 });
  shadowMat.toneMapped = false;
  const shadowPlane = new THREE.Mesh(new THREE.PlaneGeometry(1000, 1000), shadowMat);
  shadowPlane.rotation.x = -Math.PI / 2;
  shadowPlane.position.y = -300;
  shadowPlane.receiveShadow = true;
  scene.add(shadowPlane);

  // Camera poses defined at module scope (camPoses, flyTo, resolvePose)

  // Create textGroup eagerly so render loop and GUI can reference it
  textGroup = new THREE.Group();
  scene.add(textGroup);
  textGroup.userData.baseY = 0;
  textGroup.userData.camBaseY = camera.position.y;
  textGroup.userData.scrollScale = 3;
  textGroup.userData.scrollSmooth = 0.12;

  // Font + text (non-blocking — render loop starts immediately, text builds when font arrives)
  let currentFont3D = (DEFAULTS.fonts || {}).font3d || 'Bungee';
  if (DEFAULTS.fonts?.font3d) currentFont3D = DEFAULTS.fonts.font3d;
  let font = null;
  const isMobile = window.innerWidth < 768;
  const defaultSize = isMobile ? 0.6 : 1.0;
  const fontSizes = DEFAULTS.fontSizes || { holo: (DEFAULTS.holo || {}).fontSize || 0.65, proj: (DEFAULTS.proj || {}).fontSize || 0.5 };
  const fontDepths = DEFAULTS.fontDepths || { holo: (DEFAULTS.holo || {}).depth || 3.34, proj: (DEFAULTS.proj || {}).depth || 1.19 };
  // wordOpacity declared at module scope

  // Load texture maps for the 3D text
  const _texDef = DEFAULTS.textures || DEFAULTS.texParams || {};
  const texParams = { normalScale: _texDef.normalScale ?? 1, roughnessMapIntensity: 1.0, repeat: _texDef.repeat ?? 0.1 };

  // noiseGLSL, noiseParams, applyNoiseShader declared at module scope

  material = new THREE.MeshPhysicalMaterial({
    color: 0xd45e83,
    metalness: (DEFAULTS.material || DEFAULTS.mat || {}).metalness ?? 0.92,
    roughness: (DEFAULTS.material || DEFAULTS.mat || {}).roughness ?? 0.19,
    transmission: (DEFAULTS.material || DEFAULTS.mat || {}).transmission ?? 0.78,
    thickness: (DEFAULTS.material || DEFAULTS.mat || {}).thickness ?? 16.4,
    ior: (DEFAULTS.material || DEFAULTS.mat || {}).ior ?? 1.7,
    transparent: true,
  });
  applyNoiseShader(material);

  let curveSegs = DEFAULTS.curveSegs ?? 12;

  function makeTextParams(f, size, d) {
    return {
      font: f, size,
      depth: size * d,
      curveSegments: curveSegs,
      bevelEnabled: true,
      bevelThickness: size * 0.006,
      bevelSize: size * 0.004,
      bevelSegments: 3,
    };
  }

  // Create groups eagerly so GUI can reference them before font loads
  // Each word has: wordGroup (position/scale) → worldRotGroup (rotate in place) → letters
  // This order means: translate first, THEN rotate around the word's own position
  let holoGroup = new THREE.Group();
  const _holoDef = DEFAULTS.holo || {};
  holoGroup.scale.set(...(_holoDef.scale || [7.15, 3.86, 3.86]));
  holoGroup.position.set(...(_holoDef.position || _holoDef.wpos || [-34.5, -212.5, -1]));
  const holoWorldRot = new THREE.Group();
  holoGroup.add(holoWorldRot);

  let projGroup = new THREE.Group();
  const _projDef = DEFAULTS.proj || {};
  projGroup.scale.set(...(_projDef.scale || [33.2, 4, 7.8]));
  projGroup.position.set(...(_projDef.position || _projDef.wpos || [55, -123.5, 55]));
  const projWorldRot = new THREE.Group();
  projGroup.add(projWorldRot);

  textGroup.add(holoGroup);
  textGroup.add(projGroup);

  const kerningParams = { holo: 2.4, proj: 2.4, ...(DEFAULTS.kerning || {}) };

  function buildText(f) {
    fadingLetters.length = 0;

    if (!holoGroup) {
      holoGroup = new THREE.Group();
      holoGroup.scale.set(3.86, 3.86, 2);
      holoGroup.position.set(-68, -94, -1);
    } else {
      while (holoWorldRot.children.length) {
        const c = holoWorldRot.children[0];
        if (c.geometry) c.geometry.dispose();
        if (c.material && c.material !== material) c.material.dispose();
        holoWorldRot.remove(c);
      }
    }

    if (!projGroup) {
      projGroup = new THREE.Group();
      projGroup.scale.set(-10, 17.3, 2);
      projGroup.position.set(389, -100, -908);
    } else {
      while (projWorldRot.children.length) {
        const c = projWorldRot.children[0];
        if (c.geometry) c.geometry.dispose();
        if (c.material && c.material !== material) c.material.dispose();
        projWorldRot.remove(c);
      }
    }

    if (!textGroup) { textGroup = new THREE.Group(); scene.add(textGroup); }
    // holoGroup and projGroup already added via worldRot wrappers

    const holoParams = makeTextParams(f, fontSizes.holo, fontDepths.holo);
    const projParams = makeTextParams(f, fontSizes.proj, fontDepths.proj);
    const allLetters = [
      ...('HOLOGRAPHIC'.split('').map(ch => ({ ch, group: holoWorldRot, word: 'HOLOGRAPHIC', params: holoParams, size: fontSizes.holo }))),
      ...('PROJECTIONS'.split('').map((ch, i) => ({ ch, group: projWorldRot, word: 'PROJECTIONS', params: projParams, size: fontSizes.proj, posIdx: i }))).reverse(),
    ];

    // Measure spacing per word using its own size
    function measureSpacing(params, mult) {
      const geo = new TextGeometry('H', params);
      geo.center();
      geo.computeBoundingBox();
      const h = geo.boundingBox.max.y - geo.boundingBox.min.y;
      geo.dispose();
      return h * mult;
    }

    textGroup.userData.buildQueue = allLetters;
    textGroup.userData.holoSpacing = measureSpacing(holoParams, kerningParams.holo);
    textGroup.userData.projSpacing = measureSpacing(projParams, kerningParams.proj);
    textGroup.userData.buildCounts = { HOLOGRAPHIC: 0, PROJECTIONS: 0 };
  }

  // Load font async — text builds when ready, render loop runs immediately
  loadThreeFont(currentFont3D).then(f => {
    font = f;
    buildText(font);
  }).catch(e => console.error('Font load failed:', e));

  async function change3DFont(name) {
    console.log(`Loading 3D font: ${name}...`);
    try {
      const f = await loadThreeFont(name);
      font = f;
      currentFont3D = name;
      buildText(f);
      console.log(`3D font changed to: ${name}`);
    } catch (e) {
      console.error('Failed to load 3D font:', e);
    }
  }

  const _helixDef = DEFAULTS.helix || {};
  let helixSpeed = _helixDef.speed ?? 0.1;
  let helixPhaseOffset = _helixDef.phaseOffset ?? 0.16;
  let helixTwist = _helixDef.twist ?? 0.5;
  const helixParams = { speed: helixSpeed, phaseOffset: helixPhaseOffset, twist: helixTwist };
  textGroup.userData.helix = { getSpeed: () => helixSpeed, getPhase: () => helixPhaseOffset, getTwist: () => helixTwist };

  // Room environment for reflections
  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  scene.environment = pmremGenerator.fromScene(new RoomEnvironment(renderer)).texture;
  pmremGenerator.dispose();

  syncFogToTheme();

  // Initial camera set after saved state restore (see below)

  // FPS (dev only)
  if (isDev) {
    stats = new Stats();
    stats.dom.style.cssText = 'position:fixed;bottom:0;right:0;z-index:10001;';
    document.body.appendChild(stats.dom);
  }

  // ── PERSIST GUI STATE ─────────────────────
  // Keep hp_gui_collapsed in localStorage for UI state; clear old hp_gui
  localStorage.removeItem('hp_gui');
  // World position tracking (used by GUI and save/load)
  // Initialize from actual world position (accounts for textGroup transform)
  const _hwp = new THREE.Vector3(); holoGroup.getWorldPosition(_hwp);
  const holoWorldPos = { x: _hwp.x, y: _hwp.y, z: _hwp.z };
  const _pwp = new THREE.Vector3(); projGroup.getWorldPosition(_pwp);
  const projWorldPos = { x: _pwp.x, y: _pwp.y, z: _pwp.z };
  function syncHoloPos() {
    const wp = new THREE.Vector3(holoWorldPos.x, holoWorldPos.y, holoWorldPos.z);
    textGroup.updateWorldMatrix(true, false);
    textGroup.worldToLocal(wp);
    holoGroup.position.copy(wp);
  }
  function syncProjPos() {
    const wp = new THREE.Vector3(projWorldPos.x, projWorldPos.y, projWorldPos.z);
    textGroup.updateWorldMatrix(true, false);
    textGroup.worldToLocal(wp);
    projGroup.position.copy(wp);
  }

  function saveGui() {
    const s = {
      textGroup: { pos: [textGroup.position.x, textGroup.position.y, textGroup.position.z], rot: [textGroup.rotation.x, textGroup.rotation.y, textGroup.rotation.z] },
      holo: { wpos: [holoWorldPos.x, holoWorldPos.y, holoWorldPos.z], scale: [...(holoGroup._desktopScale || holoGroup.scale).toArray()], rot: [holoGroup.rotation.x, holoGroup.rotation.y, holoGroup.rotation.z], wrot: [holoWorldRot.rotation.x, holoWorldRot.rotation.y, holoWorldRot.rotation.z] },
      proj: { wpos: [projWorldPos.x, projWorldPos.y, projWorldPos.z], scale: [...(projGroup._desktopScale || projGroup.scale).toArray()], rot: [projGroup.rotation.x, projGroup.rotation.y, projGroup.rotation.z], wrot: [projWorldRot.rotation.x, projWorldRot.rotation.y, projWorldRot.rotation.z] },
      helix: { speed: helixSpeed, phaseOffset: helixPhaseOffset, twist: helixTwist },
      horizonLaser: { ...secondLaserParams, _seq: undefined },
      thirdLaser: { ...thirdLaserParams, _seq: undefined },
      mat: { color: '#' + material.color.getHexString(), metalness: material.metalness, roughness: material.roughness, transmission: material.transmission, thickness: material.thickness, ior: material.ior },
      cam: { type: cameraConfig.type, orthoZoom: cameraConfig.orthoZoom, poses: JSON.parse(JSON.stringify(camPoses)), flyDuration: flyConfig.duration, flyEnabled: flyConfig.enabled },
      renderer: { autoClear: renderer.autoClear, autoClearColor: renderer.autoClearColor, autoClearDepth: renderer.autoClearDepth, autoClearStencil: renderer.autoClearStencil },
      kerning: { ...kerningParams },
      fontSizes: { ...fontSizes },
      fontDepths: { ...fontDepths },
      curveSegs,
      texParams: { normalScale: texParams.normalScale, repeat: texParams.repeat },
      noise: { ...noiseParams },
      bloom: bloomPass ? { strength: bloomPass.strength, radius: bloomPass.radius, threshold: bloomPass.threshold, afterimage: afterimagePass.uniforms['damp'].value, smaa: smaaPass?.enabled ?? true, mobileBloom: bloomPass.enabled, mobileAfterimage: afterimagePass.enabled } : null,
      anim: { ...animParams },
      mobile: { breakpoint: mobileParams.breakpoint, enabled: mobileParams.enabled, holoPos: { ...mobileParams.holoPos }, projPos: { ...mobileParams.projPos }, holoScale: { ...mobileParams.holoScale }, projScale: { ...mobileParams.projScale }, dpr: window._hpMobileDPR ?? _mobileDPR, shadows: renderer.shadowMap.enabled },
      mouseMapping: { ...mouseMapping },
      lfos: lfos.map(l => ({ name: l.name, target: l.target, targets: l.targets, prop: l.prop, min: l.min, max: l.max, freq: l.freq, waveform: l.waveform, timeSource: l.timeSource, phase: l.phase, enabled: l.enabled, smoothing: l.smoothing, strength: l.strength, power: l.power })),
      wordOpacity: { ...wordOpacity },
      instances: JSON.parse(JSON.stringify(instanceParams)),
      fog: { color: '#' + (scene.fog?.color?.getHexString() ?? '0a1520'), density: scene.fog?.userData?.baseDensity ?? scene.fog?.density ?? 0.003 },
      shadow: { planeY: shadowPlane.position.y, opacity: shadowPlane.material.opacity, color: '#' + shadowPlane.material.color.getHexString(), visible: shadowPlane.visible, lightX: dirLight.position.x, lightY: dirLight.position.y, lightZ: dirLight.position.z, lightIntensity: dirLight.intensity, blur: dirLight.shadow.radius, bias: dirLight.shadow.bias, mapSize: dirLight.shadow.mapSize.width },
      accent: accentParams ? { hue: accentParams.hue, saturation: accentParams.saturation, harmony: accentParams.harmony, secondaryOffset: accentParams.secondaryOffset } : null,
      fonts: { ...fontParams, font3d: currentFont3D },
      guiVersion: 10,
      collapsed: gui ? collectCollapsed(gui) : undefined,
    };
    saveGui._lastState = s;
    window._hpDirty = true;
  }
  function collectCollapsed(root) {
    const state = {};
    for (const f of root.foldersRecursive()) {
      state[f._title] = f._closed;
    }
    return state;
  }
  function restoreCollapsed(root, state) {
    if (!state) return;
    for (const f of root.foldersRecursive()) {
      if (state[f._title] !== undefined) {
        f._closed ? f.close() : null;
        state[f._title] ? f.close() : f.open();
      }
    }
  }
  function loadGui() {
    return DEFAULTS;
  }
  const saved = loadGui();
  if (saved) {
    if (saved.textGroup) {
      textGroup.position.set(...saved.textGroup.pos);
      if (saved.textGroup.rot) textGroup.rotation.set(...saved.textGroup.rot);
    }
    if (saved.holo) {
      if (saved.holo.wpos) {
        holoWorldPos.x = saved.holo.wpos[0]; holoWorldPos.y = saved.holo.wpos[1]; holoWorldPos.z = saved.holo.wpos[2];
        syncHoloPos();
      } else if (saved.holo.pos) { holoGroup.position.set(...saved.holo.pos); }
      if (saved.holo.scale) holoGroup.scale.set(...saved.holo.scale);
      if (saved.holo.rot) holoGroup.rotation.set(...saved.holo.rot);
      if (saved.holo.wrot) holoWorldRot.rotation.set(...saved.holo.wrot);
    }
    if (saved.proj) {
      if (saved.proj.wpos) {
        projWorldPos.x = saved.proj.wpos[0]; projWorldPos.y = saved.proj.wpos[1]; projWorldPos.z = saved.proj.wpos[2];
        syncProjPos();
      } else if (saved.proj.pos) { projGroup.position.set(...saved.proj.pos); }
      if (saved.proj.scale) projGroup.scale.set(...saved.proj.scale);
      if (saved.proj.rot) projGroup.rotation.set(...saved.proj.rot);
      if (saved.proj.wrot) projWorldRot.rotation.set(...saved.proj.wrot);
    }
    if (saved.fog && scene.fog) { scene.fog.color.set(saved.fog.color); scene.fog.density = saved.fog.density; scene.fog.userData.baseDensity = saved.fog.density; }
    if (saved.helix) { helixSpeed = saved.helix.speed; helixPhaseOffset = saved.helix.phaseOffset; if (saved.helix.twist !== undefined) helixTwist = saved.helix.twist; }
    if (saved.horizonLaser) { Object.assign(secondLaserParams, saved.horizonLaser); }
    if (saved.thirdLaser) { Object.assign(thirdLaserParams, saved.thirdLaser); }
    if (saved.mat) { /* color is driven by accent system, not restored from save */ material.metalness = saved.mat.metalness; material.roughness = saved.mat.roughness; if (saved.mat.transmission !== undefined) material.transmission = saved.mat.transmission; if (saved.mat.thickness !== undefined) material.thickness = saved.mat.thickness; if (saved.mat.ior !== undefined) material.ior = saved.mat.ior; }
    if (saved.cam) {
      if (saved.cam.poses) {
        for (const k of Object.keys(camPoses)) {
          if (saved.cam.poses[k]) Object.assign(camPoses[k], saved.cam.poses[k]);
        }
      }
      if (saved.cam.flyDuration) flyConfig.duration = saved.cam.flyDuration;
      if (saved.cam.flyEnabled !== undefined) flyConfig.enabled = saved.cam.flyEnabled;
      if (saved.cam.type) { cameraConfig.type = saved.cam.type; switchCamera(saved.cam.type); }
      if (saved.cam.orthoZoom) {
        cameraConfig.orthoZoom = saved.cam.orthoZoom;
        const ow = window.innerWidth, oh = window.innerHeight;
        orthoCam.left = -ow / saved.cam.orthoZoom; orthoCam.right = ow / saved.cam.orthoZoom;
        orthoCam.top = oh / saved.cam.orthoZoom; orthoCam.bottom = -oh / saved.cam.orthoZoom;
        orthoCam.updateProjectionMatrix();
      }
    }
    if (saved.renderer) {
      if (saved.renderer.autoClear !== undefined) renderer.autoClear = saved.renderer.autoClear;
      if (saved.renderer.autoClearColor !== undefined) renderer.autoClearColor = saved.renderer.autoClearColor;
      if (saved.renderer.autoClearDepth !== undefined) renderer.autoClearDepth = saved.renderer.autoClearDepth;
      if (saved.renderer.autoClearStencil !== undefined) renderer.autoClearStencil = saved.renderer.autoClearStencil;
    }
    if (saved.kerning) {
      if (typeof saved.kerning === 'number') { kerningParams.holo = saved.kerning; kerningParams.proj = saved.kerning; }
      else Object.assign(kerningParams, saved.kerning);
    }
    if (saved.fontSizes) Object.assign(fontSizes, saved.fontSizes);
    if (saved.fontDepths) Object.assign(fontDepths, saved.fontDepths);
    if (saved.texParams) {
      texParams.normalScale = saved.texParams.normalScale;
      texParams.repeat = saved.texParams.repeat;
      material.normalScale.set(texParams.normalScale, texParams.normalScale);
    }
    if (saved.noise) Object.assign(noiseParams, saved.noise);
    if (saved.bloom && bloomPass) { bloomPass.strength = saved.bloom.strength; bloomPass.radius = saved.bloom.radius; bloomPass.threshold = saved.bloom.threshold; if (saved.bloom.afterimage !== undefined) afterimagePass.uniforms['damp'].value = saved.bloom.afterimage; if (smaaPass && saved.bloom.smaa !== undefined) smaaPass.enabled = saved.bloom.smaa; }
    if (saved.anim) Object.assign(animParams, saved.anim);
    if (saved.mobile) {
      mobileParams.breakpoint = saved.mobile.breakpoint;
      mobileParams.enabled = saved.mobile.enabled;
      if (saved.mobile.holoPos) Object.assign(mobileParams.holoPos, saved.mobile.holoPos);
      if (saved.mobile.projPos) Object.assign(mobileParams.projPos, saved.mobile.projPos);
      if (saved.mobile.holoScale) Object.assign(mobileParams.holoScale, saved.mobile.holoScale);
      if (saved.mobile.projScale) Object.assign(mobileParams.projScale, saved.mobile.projScale);
      applyMobileOverrides();
    }
    if (saved.mouseMapping) Object.assign(mouseMapping, saved.mouseMapping);
    // LFOs restored in GUI section below
    if (saved.wordOpacity) Object.assign(wordOpacity, saved.wordOpacity);
    if (saved.shadow) {
      shadowPlane.position.y = saved.shadow.planeY ?? -300;
      shadowPlane.material.opacity = saved.shadow.opacity ?? 0.3;
      if (saved.shadow.color) shadowPlane.material.color.set(saved.shadow.color);
      shadowPlane.visible = saved.shadow.visible ?? true;
      dirLight.position.set(saved.shadow.lightX ?? 50, saved.shadow.lightY ?? 80, saved.shadow.lightZ ?? 60);
      dirLight.intensity = saved.shadow.lightIntensity ?? 3;
      dirLight.shadow.radius = saved.shadow.blur ?? 4;
      dirLight.shadow.bias = saved.shadow.bias ?? -0.001;
      if (saved.shadow.mapSize) { dirLight.shadow.mapSize.width = saved.shadow.mapSize; dirLight.shadow.mapSize.height = saved.shadow.mapSize; }
    }
    if (saved.instances) {
      if (saved.instances.holo) { Object.assign(instanceParams.holo, saved.instances.holo); instanceParams.holo.posSpread = { ...instanceParams.holo.posSpread }; instanceParams.holo.rotSpread = { ...instanceParams.holo.rotSpread }; }
      if (saved.instances.proj) { Object.assign(instanceParams.proj, saved.instances.proj); instanceParams.proj.posSpread = { ...instanceParams.proj.posSpread }; instanceParams.proj.rotSpread = { ...instanceParams.proj.rotSpread }; }
    }
  }

  applyMobileOverrides(); // adjust word X positions for current viewport width

  // Apply home pose (after saved state has been restored)
  {
    const p = resolvePose('home');
    camera.position.set(p.x, p.y, p.z);
    camera.rotation.set(p.rx, p.ry, p.rz);
    if (camera.isPerspectiveCamera) { camera.fov = p.fov; }
    if (camera.isOrthographicCamera) {
      cameraConfig.orthoZoom = p.oz;
      const w = window.innerWidth, h = window.innerHeight;
      camera.left = -w / p.oz; camera.right = w / p.oz;
      camera.top = h / p.oz; camera.bottom = -h / p.oz;
    }
    camera.updateProjectionMatrix();
  }

  // ── DEBUG GUI (dev only) ─────────────────
  const gui = isDev ? new GUI({ title: 'Scene Controls' }) : null;
  // Wrap saveGui so every onChange handler saves automatically
  function withSave(fn) { return function(...args) { fn(...args); saveGui(); }; }
  if (gui) gui.onChange(saveGui);

  function addAccentControl() {
    const harmonyModes = ['complementary', 'analogous', 'analogous-2', 'triadic', 'split-comp', 'custom'];
    const accentParams = { hue: 287, saturation: 58, harmony: 'analogous-2', secondaryOffset: 150, ...(DEFAULTS.accent || {}) };
    if (saved?.accent) {
      accentParams.hue = saved.accent.hue;
      accentParams.saturation = saved.accent.saturation;
      if (saved.accent.harmony) accentParams.harmony = saved.accent.harmony;
      if (saved.accent.secondaryOffset !== undefined) accentParams.secondaryOffset = saved.accent.secondaryOffset;
    }

    function getSecondaryHue(primaryHue) {
      if (accentParams.harmony === 'custom') return (primaryHue + accentParams.secondaryOffset) % 360;
      // Simple hue offsets (replaces expensive M3 TemperatureCache)
      switch (accentParams.harmony) {
        case 'complementary': return (primaryHue + 180) % 360;
        case 'analogous': return (primaryHue + 30) % 360;
        case 'analogous-2': return (primaryHue + 60) % 360;
        case 'triadic': return (primaryHue + 120) % 360;
        case 'split-comp': return (primaryHue + 210) % 360;
        default: return (primaryHue + 150) % 360;
      }
    }

    let _accentCacheKey = null;
    function applyAccent() {
      const h = accentParams.hue;
      const s = accentParams.saturation;
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

      const hi = Math.round(h) % 360;
      const si = Math.round(s);
      const cacheKey = (hi << 16) | (si << 8) | (isDark ? 1 : 0);
      if (_accentCacheKey === cacheKey) return;
      _accentCacheKey = cacheKey;

      // Just set the 3 root variables — CSS computes all colors via hsl(var(...))
      const el = document.documentElement;
      const sh = getSecondaryHue(h);
      el.style.setProperty('--accent-h', h);
      el.style.setProperty('--accent-s', s + '%');
      el.style.setProperty('--secondary-h', sh);

      // Non-CSS updates: fog, 3D text, logo
      const ns = Math.max(s * 0.15, 5);
      const fogColor = `hsl(${h},${ns}%,${isDark ? 6 : 87}%)`;
      const sat = s / 100;
      const textL = isDark ? 0.6 : 0.35;
      const priHue = h / 360;
      const secHue = sh / 360;
      primaryColor3D.setHSL(priHue, sat, textL, THREE.SRGBColorSpace);
      tertiaryColor3D.setHSL(secHue, sat, textL, THREE.SRGBColorSpace);
      material.color.copy(primaryColor3D);
      if (textGroup && textGroup.children.length >= 2) {
        // Word 0 = HOLOGRAPHIC → primary gradient
        const holoRot = textGroup.children[0]?.children[0];
        if (holoRot) {
          const letters = holoRot.children.filter(c => c.isMesh && !c.isInstancedMesh);
          const len = letters.length;
          const holoEnd = new THREE.Color().copy(primaryColor3D).offsetHSL(0.05, 0, -0.1);
          letters.forEach((mesh, i) => {
            const t = i / (len - 1 || 1);
            mesh.material.color.copy(primaryColor3D).lerp(holoEnd, t);
          });
        }
        // Word 1 = PROJECTIONS → secondary/harmonious gradient
        const projRot = textGroup.children[1]?.children[0];
        if (projRot) {
          const letters = projRot.children.filter(c => c.isMesh && !c.isInstancedMesh);
          const len = letters.length;
          const projEnd = new THREE.Color().copy(tertiaryColor3D).offsetHSL(0.05, 0, -0.1);
          letters.forEach((mesh, i) => {
            const t = i / (len - 1 || 1);
            mesh.material.color.copy(tertiaryColor3D).lerp(projEnd, t);
          });
        }
      }
      // Sync instanced mesh colors
      for (const im of instancedMeshes) {
        if (!im.material || !im._wordGroup) continue;
        const baseColor = im._wordGroup === textGroup.children[0] ? primaryColor3D : tertiaryColor3D;
        im.material.color.copy(baseColor);
      }
      // Fog + page bg

      if (scene.fog) {
        scene.fog.color.setStyle(fogColor);
        // Light mode: reduce fog to prevent dark text washout against light background
        const baseDensity = scene.fog.userData?.baseDensity ?? scene.fog.density;
        scene.fog.density = isDark ? baseDensity : baseDensity * 0.4;
      }
      // Scene background = same color as fog so the canvas acts as the page backdrop.
      // This bypasses any html-show-through reliability issues across browsers.
      if (scene.background && scene.background.isColor) scene.background.setStyle(fogColor);
      else scene.background = new THREE.Color().setStyle(fogColor);
      // Flush afterimage buffer only on actual theme change
      if (afterimagePass && applyAccent._lastIsDark !== undefined && applyAccent._lastIsDark !== isDark) {
        const savedDamp = afterimagePass.uniforms['damp'].value || applyAccent._baseDamp || 0.84;
        applyAccent._baseDamp = savedDamp;
        afterimagePass.uniforms['damp'].value = 0;
        clearTimeout(applyAccent._dampTimer);
        applyAccent._dampTimer = setTimeout(() => { afterimagePass.uniforms['damp'].value = savedDamp; }, 500);
      }
      applyAccent._lastIsDark = isDark;
      // Adjust shadow visibility per theme
      if (shadowPlane) shadowPlane.material.opacity = isDark ? 0.3 : 0.5;
      // Logo colors: reversed from 3D text (H=tertiary, P=primary)
      const hHex = '#' + tertiaryColor3D.getHexString();
      const pHex = '#' + primaryColor3D.getHexString();
      window._hpLogoColors = { h: hHex, p: pHex };
      document.querySelectorAll('.logo-h').forEach(el => { el.style.color = hHex; });
      document.querySelectorAll('.logo-p').forEach(el => { el.style.color = pHex; });
    }
    applyAccent();
    window._hpApplyAccent = applyAccent;
    window._hpAccentParams = accentParams;
    // Throttle accent updates — coalesce slider ticks to one update per frame, debounce save
    let accentDirty = false, accentSaveTimer = null;
    function accentThrottled() {
      accentDirty = true;
      if (!accentThrottled._raf) {
        accentThrottled._raf = requestAnimationFrame(() => {
          accentThrottled._raf = null;
          if (accentDirty) { applyAccent(); accentDirty = false; }
        });
      }
      clearTimeout(accentSaveTimer);
      accentSaveTimer = setTimeout(saveGui, 300);
    }
    if (gui) {
      const accF = gui.addFolder('Accent Color');
      window._hpAccentGui = accF;
      lfoGuiControllers.accentHue = accF.add(accentParams, 'hue', 0, 360, 1).name('primary hue').onChange(accentThrottled);
      accF.add(accentParams, 'harmony', harmonyModes).name('harmony').onChange(accentThrottled);
      accF.add(accentParams, 'secondaryOffset', 0, 360, 1).name('custom offset').onChange(accentThrottled);
      lfoGuiControllers.accentSat = accF.add(accentParams, 'saturation', 0, 100, 1).name('saturation').onChange(accentThrottled);
    }
    return accentParams;
  }
  const accentParams = addAccentControl();

  if (gui) {
  const textF2 = gui.addFolder('Text Group');
  textF2.add(textGroup.position, 'x', -500, 500, 1).name('x');
  textF2.add(textGroup.position, 'y', -500, 500, 1).name('y');
  textF2.add(textGroup.position, 'z', -500, 500, 1).name('z');
  textF2.add(textGroup.rotation, 'x', -Math.PI, Math.PI, 0.01).name('rot x');
  textF2.add(textGroup.rotation, 'y', -Math.PI, Math.PI, 0.01).name('rot y');
  textF2.add(textGroup.rotation, 'z', -Math.PI, Math.PI, 0.01).name('rot z');

  const shadowF = gui.addFolder('Shadows');
  shadowF.add(shadowPlane, 'visible').name('enabled');
  shadowF.add(shadowPlane.position, 'y', -1000, 100, 1).name('plane height');
  const _shadowSize = { size: 1000 };
  shadowF.add(_shadowSize, 'size', 100, 5000, 50).name('plane size').onChange(withSave(v => {
    shadowPlane.geometry.dispose();
    shadowPlane.geometry = new THREE.PlaneGeometry(v, v);
  }));
  shadowF.add(shadowPlane.material, 'opacity', 0, 1, 0.01).name('opacity');
  shadowF.addColor({ color: '#' + shadowPlane.material.color.getHexString() }, 'color').name('color').onChange(withSave(v => shadowPlane.material.color.set(v)));
  lfoGuiControllers.shadowLightIntensity = shadowF.add(dirLight, 'intensity', 0, 10, 0.1).name('light intensity');
  shadowF.add(dirLight.position, 'x', -500, 500, 1).name('light x');
  shadowF.add(dirLight.position, 'y', -500, 500, 1).name('light y');
  shadowF.add(dirLight.position, 'z', -500, 500, 1).name('light z');
  shadowF.add(dirLight.shadow, 'radius', 0, 30, 0.5).name('blur');
  shadowF.add(dirLight.shadow, 'bias', -0.01, 0.01, 0.0001).name('bias');
  shadowF.add({ mapSize: dirLight.shadow.mapSize.width }, 'mapSize', [512, 1024, 2048, 4096]).name('map size').onChange(withSave(v => {
    dirLight.shadow.mapSize.width = v; dirLight.shadow.mapSize.height = v;
    dirLight.shadow.map?.dispose(); dirLight.shadow.map = null;
  }));

  const holoF = gui.addFolder('HOLOGRAPHIC');
  lfoGuiControllers.holoPosX = holoF.add(holoWorldPos, 'x', -500, 500, 0.5).name('x (world)').onChange(withSave(syncHoloPos));
  lfoGuiControllers.holoPosY = holoF.add(holoWorldPos, 'y', -500, 500, 0.5).name('y (world)').onChange(withSave(syncHoloPos));
  lfoGuiControllers.holoPosZ = holoF.add(holoWorldPos, 'z', -500, 500, 0.5).name('z (world)').onChange(withSave(syncHoloPos));
  lfoGuiControllers.holoScaleX = holoF.add(holoGroup.scale, 'x', 0.01, 40, 0.01).name('scale X');
  lfoGuiControllers.holoScaleY = holoF.add(holoGroup.scale, 'y', 0.01, 40, 0.01).name('scale Y');
  lfoGuiControllers.holoScaleZ = holoF.add(holoGroup.scale, 'z', 0.01, 40, 0.01).name('scale Z');
  holoF.add(fontSizes, 'holo', 0.1, 5, 0.05).name('font size').onChange(withSave(() => { if (font) buildText(font); }));
  holoF.add(fontDepths, 'holo', 0.01, 5, 0.01).name('depth').onChange(withSave(() => { if (font) buildText(font); }));
  lfoGuiControllers.holoRotX = holoF.add(holoGroup.rotation, 'x', -Math.PI, Math.PI, 0.01).name('local rot x');
  lfoGuiControllers.holoRotY = holoF.add(holoGroup.rotation, 'y', -Math.PI, Math.PI, 0.01).name('local rot y');
  lfoGuiControllers.holoRotZ = holoF.add(holoGroup.rotation, 'z', -Math.PI, Math.PI, 0.01).name('local rot z');
  holoF.add(holoWorldRot.rotation, 'x', -Math.PI, Math.PI, 0.01).name('world rot x');
  holoF.add(holoWorldRot.rotation, 'y', -Math.PI, Math.PI, 0.01).name('world rot y');
  holoF.add(holoWorldRot.rotation, 'z', -Math.PI, Math.PI, 0.01).name('world rot z');
  holoF.add(kerningParams, 'holo', 0.5, 3, 0.05).name('kerning').onChange(withSave(() => { if (font) buildText(font); }));
  const holoInstF = holoF.addFolder('Instances');
  const holoInstRebuild = withSave(() => rebuildInstances(holoGroup, instanceParams.holo));
  holoInstF.add(instanceParams.holo, 'count', 1, 100, 1).name('count').onFinishChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo.posSpread, 'x', 0, 50, 0.1).name('pos spread X').onChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo.posSpread, 'y', 0, 50, 0.1).name('pos spread Y').onChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo.posSpread, 'z', 0, 50, 0.1).name('pos spread Z').onChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo.rotSpread, 'x', 0, Math.PI, 0.01).name('rot spread X').onChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo.rotSpread, 'y', 0, Math.PI, 0.01).name('rot spread Y').onChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo.rotSpread, 'z', 0, Math.PI, 0.01).name('rot spread Z').onChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo, 'opacityMin', 0, 1, 0.01).name('opacity min').onChange(holoInstRebuild);
  holoInstF.add(instanceParams.holo, 'opacityMax', 0, 1, 0.01).name('opacity max').onChange(holoInstRebuild);
  lfoGuiControllers.holoOpacity = holoF.add(wordOpacity, 'holo', 0, 1, 0.01).name('opacity').onChange(withSave(v => {
    holoWorldRot.children.forEach(m => { if (m.isMesh) { m.material.opacity = v; m.material.transparent = v < 1; } });
  }));

  const projF = gui.addFolder('PROJECTIONS');
  lfoGuiControllers.projPosX = projF.add(projWorldPos, 'x', -500, 500, 0.5).name('x (world)').onChange(withSave(syncProjPos));
  lfoGuiControllers.projPosY = projF.add(projWorldPos, 'y', -500, 500, 0.5).name('y (world)').onChange(withSave(syncProjPos));
  lfoGuiControllers.projPosZ = projF.add(projWorldPos, 'z', -500, 500, 0.5).name('z (world)').onChange(withSave(syncProjPos));
  lfoGuiControllers.projScaleX = projF.add(projGroup.scale, 'x', 0.01, 40, 0.01).name('scale X');
  lfoGuiControllers.projScaleY = projF.add(projGroup.scale, 'y', 0.01, 40, 0.01).name('scale Y');
  lfoGuiControllers.projScaleZ = projF.add(projGroup.scale, 'z', 0.01, 40, 0.01).name('scale Z');
  projF.add(fontSizes, 'proj', 0.1, 5, 0.05).name('font size').onChange(withSave(() => { if (font) buildText(font); }));
  projF.add(fontDepths, 'proj', 0.01, 5, 0.01).name('depth').onChange(withSave(() => { if (font) buildText(font); }));
  lfoGuiControllers.projRotX = projF.add(projGroup.rotation, 'x', -Math.PI, Math.PI, 0.01).name('local rot x');
  lfoGuiControllers.projRotY = projF.add(projGroup.rotation, 'y', -Math.PI, Math.PI, 0.01).name('local rot y');
  lfoGuiControllers.projRotZ = projF.add(projGroup.rotation, 'z', -Math.PI, Math.PI, 0.01).name('local rot z');
  projF.add(projWorldRot.rotation, 'x', -Math.PI, Math.PI, 0.01).name('world rot x');
  projF.add(projWorldRot.rotation, 'y', -Math.PI, Math.PI, 0.01).name('world rot y');
  projF.add(projWorldRot.rotation, 'z', -Math.PI, Math.PI, 0.01).name('world rot z');
  projF.add(kerningParams, 'proj', 0.5, 3, 0.05).name('kerning').onChange(withSave(() => { if (font) buildText(font); }));
  const projInstF = projF.addFolder('Instances');
  const projInstRebuild = withSave(() => rebuildInstances(projGroup, instanceParams.proj));
  projInstF.add(instanceParams.proj, 'count', 1, 100, 1).name('count').onFinishChange(projInstRebuild);
  projInstF.add(instanceParams.proj.posSpread, 'x', 0, 50, 0.1).name('pos spread X').onChange(projInstRebuild);
  projInstF.add(instanceParams.proj.posSpread, 'y', 0, 50, 0.1).name('pos spread Y').onChange(projInstRebuild);
  projInstF.add(instanceParams.proj.posSpread, 'z', 0, 50, 0.1).name('pos spread Z').onChange(projInstRebuild);
  projInstF.add(instanceParams.proj.rotSpread, 'x', 0, Math.PI, 0.01).name('rot spread X').onChange(projInstRebuild);
  projInstF.add(instanceParams.proj.rotSpread, 'y', 0, Math.PI, 0.01).name('rot spread Y').onChange(projInstRebuild);
  projInstF.add(instanceParams.proj.rotSpread, 'z', 0, Math.PI, 0.01).name('rot spread Z').onChange(projInstRebuild);
  projInstF.add(instanceParams.proj, 'opacityMin', 0, 1, 0.01).name('opacity min').onChange(projInstRebuild);
  projInstF.add(instanceParams.proj, 'opacityMax', 0, 1, 0.01).name('opacity max').onChange(projInstRebuild);
  lfoGuiControllers.projOpacity = projF.add(wordOpacity, 'proj', 0, 1, 0.01).name('opacity').onChange(withSave(v => {
    projWorldRot.children.forEach(m => { if (m.isMesh) { m.material.opacity = v; m.material.transparent = v < 1; } });
  }));
  projF.add({ copyFromHolo() {
    projWorldPos.x = holoWorldPos.x; projWorldPos.y = holoWorldPos.y; projWorldPos.z = holoWorldPos.z;
    syncProjPos();
    projGroup.scale.copy(holoGroup.scale);
    projGroup.rotation.copy(holoGroup.rotation);
    projWorldRot.rotation.copy(holoWorldRot.rotation);
    fontSizes.proj = fontSizes.holo;
    fontDepths.proj = fontDepths.holo;
    kerningParams.proj = kerningParams.holo;
    wordOpacity.proj = wordOpacity.holo;
    if (font) buildText(font);
    projF.controllersRecursive().forEach(c => c.updateDisplay());
    saveGui();
  }}, 'copyFromHolo').name('Copy from HOLOGRAPHIC');

  const bloomF = gui.addFolder('Post-processing');
  bloomF.add(bloomPass, 'strength', 0, 3, 0.01).name('bloom strength');
  bloomF.add(bloomPass, 'radius', 0, 2, 0.01).name('bloom radius');
  bloomF.add(bloomPass, 'threshold', 0, 1, 0.01).name('bloom threshold');
  bloomF.add(afterimagePass.uniforms['damp'], 'value', 0, 0.99, 0.01).name('afterimage (trail)');
  bloomF.add(smaaPass, 'enabled').name('SMAA antialiasing');
  bloomF.add(bloomPass, 'enabled').name('bloom');
  bloomF.add(afterimagePass, 'enabled').name('afterimage');
  bloomF.add(renderer.shadowMap, 'enabled').name('shadows');
  const _mobilePerf = { dpr: _mobileDPR };
  window._hpMobileDPR = _mobileDPR;
  bloomF.add(_mobilePerf, 'dpr', 0.5, 3, 0.25).name('mobile DPR').onChange(v => {
    window._hpMobileDPR = v;
    if (window.innerWidth < (mobileParams?.breakpoint || 768)) {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, v));
      composer.setSize(window.innerWidth, window.innerHeight);
    }
  });

  const fogF = gui.addFolder('Fog');
  const fogConfig = { type: 'exp2' };
  fogF.add(fogConfig, 'type', ['none', 'linear', 'exp2']).name('type').onChange(withSave(v => {
    const color = scene.fog ? scene.fog.color.clone() : new THREE.Color(0x000000);
    if (v === 'none') { scene.fog = null; }
    else if (v === 'linear') { scene.fog = new THREE.Fog(color, 100, 2000); scene.fog.userData = { baseDensity: 0 }; }
    else { scene.fog = new THREE.FogExp2(color, 0.0012); scene.fog.userData = { baseDensity: 0.0012 }; }
  }));
  fogF.addColor({ color: '#' + scene.fog.color.getHexString() }, 'color').name('color').onChange(withSave(v => {
    if (scene.fog) scene.fog.color.set(v);
    document.documentElement.style.background = v;
  }));
  lfoGuiControllers.fogDensity = fogF.add(scene.fog, 'density', 0, 0.002, 0.00001).name('density (exp2)').onChange(withSave(v => { if (scene.fog?.userData) scene.fog.userData.baseDensity = v; }));
  fogF.add({ near: 100 }, 'near', 0, 1000, 1).name('near (linear)').onChange(withSave(v => { if (scene.fog?.isFog) scene.fog.near = v; }));
  fogF.add({ far: 2000 }, 'far', 100, 10000, 10).name('far (linear)').onChange(withSave(v => { if (scene.fog?.isFog) scene.fog.far = v; }));

  const textF = gui.addFolder('Text Geometry');
  textF.add({ curveSegs }, 'curveSegs', 8, 256, 8).name('curve segments').onChange(withSave(v => {
    curveSegs = v;
    buildText(font);
  }));

  const matF = gui.addFolder('Text Material');
  function updateAllMats(fn) { if (textGroup) textGroup.traverse(c => { if (c.isMesh && c.material) fn(c.material); }); }
  matF.addColor({ color: '#' + material.color.getHexString() }, 'color').name('color').onChange(withSave(v => { material.color.set(v); updateAllMats(m => m.color.set(v)); }));
  matF.add(material, 'metalness', 0, 1, 0.01).onChange(withSave(v => updateAllMats(m => { m.metalness = v; })));
  matF.add(material, 'roughness', 0, 1, 0.01).onChange(withSave(v => updateAllMats(m => { m.roughness = v; })));
  matF.add(material, 'transmission', 0, 1, 0.01).onChange(withSave(v => updateAllMats(m => { m.transmission = v; })));
  matF.add(material, 'thickness', 0, 20, 0.1).onChange(withSave(v => updateAllMats(m => { m.thickness = v; })));
  matF.add(material, 'ior', 1, 2.33, 0.01).onChange(withSave(v => updateAllMats(m => { m.ior = v; })));
  matF.add(noiseParams, 'speed', 0, 50, 0.01).name('noise speed').onChange(withSave(v => {
    updateAllMats(m => { if (m.userData.shader) m.userData.shader.uniforms.uNoiseSpeed.value = v; });
  }));
  matF.add(noiseParams, 'scale', 0.01, 20, 0.01).name('noise scale').onChange(withSave(v => {
    updateAllMats(m => { if (m.userData.shader) m.userData.shader.uniforms.uNoiseScale.value = v; });
  }));
  matF.add(noiseParams, 'intensity', 0, 2, 0.01).name('noise intensity').onChange(withSave(v => {
    updateAllMats(m => { if (m.userData.shader) m.userData.shader.uniforms.uNoiseIntensity.value = v; });
  }));

  const animF = gui.addFolder('Animation');
  animF.add(helixParams, 'speed', 0, 5, 0.1).name('helix speed').onChange(withSave(v => { helixSpeed = v; }));
  animF.add(helixParams, 'phaseOffset', 0.05, 2, 0.01).name('helix phase').onChange(withSave(v => { helixPhaseOffset = v; }));
  lfoGuiControllers.helixTwist = animF.add(helixParams, 'twist', -Math.PI, Math.PI, 0.01).name('helix twist').onChange(withSave(v => { helixTwist = v; }));
  animF.add(animParams, 'letterInterval', 0, 2000, 10).name('letter interval (ms)');
  animF.add(animParams, 'fadeTime', 50, 5000, 50).name('fade time (ms)');
  if (!animParams.lfoBlendTime) animParams.lfoBlendTime = 3000;
  animF.add(animParams, 'lfoBlendTime', 500, 10000, 100).name('LFO blend (ms)');
  if (!animParams.smoothingRamp) animParams.smoothingRamp = 3000;
  animF.add(animParams, 'smoothingRamp', 0, 10000, 100).name('smoothing ramp (ms)');
  animF.add(flyConfig, 'duration', 0.3, 5, 0.1).name('fly time (s)');
  lfoGuiControllers.timeScale = animF.add(timeParams, 'scale', -2, 5, 0.01).name('global time');
  lfoGuiControllers.laserTimeScale = animF.add(timeParams, 'laserScale', -2, 5, 0.01).name('laser time');
  lfoGuiControllers.textTimeScale = animF.add(timeParams, 'textScale', -2, 5, 0.01).name('text time');

  const mobileF = gui.addFolder('Mobile');
  const mobileUpdate = withSave(() => applyMobileOverrides());
  mobileF.add(mobileParams, 'enabled').name('enable overrides').onChange(mobileUpdate);
  mobileF.add(mobileParams, 'breakpoint', 320, 1200, 10).name('breakpoint');
  const mhF = mobileF.addFolder('HOLOGRAPHIC pos');
  mhF.add(mobileParams.holoPos, 'x', -500, 500, 0.5).name('x').onChange(mobileUpdate);
  mhF.add(mobileParams.holoPos, 'y', -500, 500, 0.5).name('y').onChange(mobileUpdate);
  mhF.add(mobileParams.holoPos, 'z', -500, 500, 0.5).name('z').onChange(mobileUpdate);
  const mhsF = mobileF.addFolder('HOLOGRAPHIC scale');
  mhsF.add(mobileParams.holoScale, 'x', 0.1, 5, 0.01).name('x').onChange(mobileUpdate);
  mhsF.add(mobileParams.holoScale, 'y', 0.1, 5, 0.01).name('y').onChange(mobileUpdate);
  mhsF.add(mobileParams.holoScale, 'z', 0.1, 5, 0.01).name('z').onChange(mobileUpdate);
  const mpF = mobileF.addFolder('PROJECTIONS pos');
  mpF.add(mobileParams.projPos, 'x', -500, 500, 0.5).name('x').onChange(mobileUpdate);
  mpF.add(mobileParams.projPos, 'y', -500, 500, 0.5).name('y').onChange(mobileUpdate);
  mpF.add(mobileParams.projPos, 'z', -500, 500, 0.5).name('z').onChange(mobileUpdate);
  const mpsF = mobileF.addFolder('PROJECTIONS scale');
  mpsF.add(mobileParams.projScale, 'x', 0.1, 5, 0.01).name('x').onChange(mobileUpdate);
  mpsF.add(mobileParams.projScale, 'y', 0.1, 5, 0.01).name('y').onChange(mobileUpdate);
  mpsF.add(mobileParams.projScale, 'z', 0.1, 5, 0.01).name('z').onChange(mobileUpdate);

  // Mouse → Hue now handled via Functions (mouseX waveform → accentHue target)

  // LFO controls
  const lfoF = gui.addFolder('Functions');
  lfoF.add(fnGlobal, 'enabled').name('all enabled');
  // Function param names that can be modulated
  const FN_PARAMS = ['strength', 'power', 'freq', 'min', 'max', 'phase', 'smoothing', 'waveform'];
  const MAX_FNS = 10;
  // Build target names: scene params + fn:N:param for cross-modulation
  const sceneTargets = [
    'holoOpacity', 'projOpacity',
    'holoPosX', 'holoPosY', 'holoPosZ',
    'holoScaleX', 'holoScaleY', 'holoScaleZ',
    'holoRotX', 'holoRotY', 'holoRotZ',
    'projPosX', 'projPosY', 'projPosZ',
    'projScaleX', 'projScaleY', 'projScaleZ',
    'projRotX', 'projRotY', 'projRotZ',
    'fogDensity', 'accentHue', 'accentSat', 'timeScale', 'laserTimeScale', 'textTimeScale', 'helixTwist',
    'shadowLightIntensity', 'shadowLightX', 'shadowLightY', 'shadowLightZ',
    'camPosX', 'camPosY', 'camPosZ', 'camRotX', 'camRotY', 'camRotZ',
    'noiseSpeed', 'noiseScale', 'noiseIntensity',
    'laserGroupX', 'laserGroupY', 'laserGroupZ',
    'textPosX', 'textPosY', 'textPosZ',
    'textRotX', 'textRotY', 'textRotZ',
    'holoInstVisibility', 'projInstVisibility',
    'holoInstSpreadX', 'holoInstSpreadY', 'holoInstSpreadZ',
    'holoInstRotSpreadX', 'holoInstRotSpreadY', 'holoInstRotSpreadZ',
    'holoInstOpacityMin', 'holoInstOpacityMax',
    'projInstSpreadX', 'projInstSpreadY', 'projInstSpreadZ',
    'projInstRotSpreadX', 'projInstRotSpreadY', 'projInstRotSpreadZ',
    'projInstOpacityMin', 'projInstOpacityMax',
  ];
  // Build fn target names using each function's display name
  function buildFnTargetNames() {
    const names = [];
    for (let i = 0; i < lfos.length; i++) {
      const label = lfos[i].name;
      for (const p of FN_PARAMS) names.push(`fn:${label}:${p}`);
    }
    return names;
  }
  let lfoTargetNames = [...sceneTargets, ...buildFnTargetNames()];

  // Dynamic fn target resolver — finds LFO by name, sets param
  // resolveFnTarget declared at module scope

  // Register fn:N:param resolver
  for (let i = 0; i < MAX_FNS; i++) {
    for (const p of FN_PARAMS) {
      const key = `fn:${i}:${p}`;
      lfoTargets[key] = {
        set: (_, v) => {
          if (!lfos[i]) return;
          if (p === 'waveform') {
            // Map 0..1 to waveform index
            const idx = Math.min(Math.floor(v * WAVEFORMS.length), WAVEFORMS.length - 1);
            lfos[i].waveform = WAVEFORMS[idx];
          } else {
            lfos[i][p] = v;
          }
        },
      };
    }
  }
  const lfoAdd = { target: lfoTargetNames[0] || 'holoOpacity', min: 0, max: 1, freq: 0.5, waveform: 'sine', phase: 0, strength: 1, power: 1, smoothing: 0.05 };
  const lfoAddF = lfoF.addFolder('Add LFO');
  let lfoAddTargetCtrl = lfoAddF.add(lfoAdd, 'target', lfoTargetNames).name('target');
  lfoAddF.add(lfoAdd, 'min', -360, 360, 0.0001).name('min');
  lfoAddF.add(lfoAdd, 'max', -360, 360, 0.0001).name('max');
  lfoAddF.add(lfoAdd, 'freq', 0.001, 100, 0.001).name('freq (Hz)');
  lfoAddF.add(lfoAdd, 'waveform', WAVEFORMS).name('function');
  lfoAdd.timeSource = 'global';
  lfoAddF.add(lfoAdd, 'timeSource', ['global', 'laser', 'text']).name('time');
  lfoAddF.add(lfoAdd, 'phase', 0, Math.PI * 2, 0.01).name('phase');
  lfoAddF.add(lfoAdd, 'strength', 0, 10, 0.001).name('strength');
  lfoAddF.add(lfoAdd, 'power', 0.1, 10, 0.1).name('power');
  lfoAddF.add(lfoAdd, 'smoothing', 0, 100, 1).name('smoothing');
  lfoAddF.add({ add() {
    const lfo = createLFO({ name: `${lfoAdd.target} ${lfos.length}`, target: lfoAdd.target, prop: null, min: lfoAdd.min, max: lfoAdd.max, freq: lfoAdd.freq, waveform: lfoAdd.waveform, timeSource: lfoAdd.timeSource, phase: lfoAdd.phase, strength: lfoAdd.strength, power: lfoAdd.power, smoothing: lfoAdd.smoothing });
    lfos.push(lfo);
    buildFnGui(lfo);
    saveGui();
    updateLfoDisabledState();
  }}, 'add').name('+ Add Function');

  function refreshFnTargetNames() {
    lfoTargetNames = [...sceneTargets, ...buildFnTargetNames()];
    // Rebuild the "Add" form target dropdown with updated names
    if (lfoAddTargetCtrl) {
      const parent = lfoAddTargetCtrl.domElement.parentElement;
      lfoAddTargetCtrl.destroy();
      lfoAddTargetCtrl = lfoAddF.add(lfoAdd, 'target', lfoTargetNames).name('target');
      // Move it to the top of the folder
      if (parent) parent.prepend(lfoAddTargetCtrl.domElement);
    }
  }

  function buildFnGui(lfo) {
    refreshFnTargetNames();
    const f = lfoF.addFolder(lfo.name);
    f.add(lfo, 'enabled').name('enabled').onChange(() => { saveGui(); updateLfoDisabledState(); });
    f.add(lfo, 'target', lfoTargetNames).name('target').onChange(() => { saveGui(); updateLfoDisabledState(); });
    f.add(lfo, 'strength', 0, 10, 0.001).name('strength');
    f.add(lfo, 'power', 0.1, 10, 0.1).name('power');
    f.add(lfo, 'min', -360, 360, 0.0001).name('min');
    f.add(lfo, 'max', -360, 360, 0.0001).name('max');
    f.add(lfo, 'freq', 0.001, 100, 0.001).name('freq');
    f.add(lfo, 'waveform', WAVEFORMS).name('function');
    f.add(lfo, 'timeSource', ['global', 'laser', 'text']).name('time');
    f.add(lfo, 'phase', 0, Math.PI * 2, 0.01).name('phase');
    f.add(lfo, 'smoothing', 0, 100, 1).name('smoothing (LPF)');
    // Additional targets
    const addTgtState = { target: lfoTargetNames[0] };
    const tgtF = f.addFolder('+ targets');
    tgtF.add(addTgtState, 'target', lfoTargetNames).name('target');
    function addTargetButton(name) {
      const ctrl = tgtF.add({ remove() { const idx = lfo.targets.indexOf(name); if (idx >= 0) lfo.targets.splice(idx, 1); ctrl.destroy(); saveGui(); updateLfoDisabledState(); } }, 'remove').name(name + ' ✕');
    }
    tgtF.add({ add() {
      const name = addTgtState.target;
      lfo.targets.push(name);
      addTargetButton(name);
      saveGui(); updateLfoDisabledState();
    }}, 'add').name('+ add target');
    // Restore saved additional targets
    if (lfo.targets.length) {
      for (const tName of [...lfo.targets]) addTargetButton(tName);
    }
    f.add({ remove() { const idx = lfos.indexOf(lfo); if (idx >= 0) lfos.splice(idx, 1); render._sortedLfos = null; f.destroy(); saveGui(); updateLfoDisabledState(); } }, 'remove').name('delete');
    return f;
  }

  function addSeqLaserGUI(folder, params, builtFlag) {
    folder.add(params, 'enabled').name('enabled');
    folder.add(params, 'poolSize', 1, 500, 1).name('beam pool').onChange(withSave(() => { if (builtFlag) builtFlag.v = false; }));
    folder.add(params, 'spawnRate', 0.1, 2, 0.01).name('speed (wait scale)');
    folder.add(params, 'stepMin', 1, 1000, 1).name('step min');
    folder.add(params, 'stepMax', 1, 10000, 1).name('step max');
    folder.add(params, 'stepFreq', 0.01, 5, 0.01).name('step freq (Hz)');
    folder.add(params, 'stepPhase', 0, Math.PI * 2, 0.01).name('step phase');
    folder.add(params, 'targetsMax', 0, 200, 1).name('jitter max');
    folder.add(params, 'boltLifeMin', 0.02, 2, 0.01).name('bolt life min');
    folder.add(params, 'boltLifeMax', 0.05, 3, 0.01).name('bolt life max');
    folder.add(params, 'colorSource', ['primary', 'tertiary', 'primary-tertiary', 'tertiary-primary', 'complementary', 'analogous', 'white']).name('color');
    folder.add(params, 'opacityMin', 0, 1, 0.01).name('opacity min');
    folder.add(params, 'opacityMax', 0, 1, 0.01).name('opacity max');
    folder.add(params, 'originX', -500, 500, 1).name('origin X');
    folder.add(params, 'originY', -200, 300, 1).name('origin Y');
    folder.add(params, 'originZ', -2000, 200, 1).name('origin Z');
    folder.add(params, 'opacityPulseFreq', 0, 5, 0.01).name('opacity pulse Hz');
    folder.add(params, 'opacityPulseMin', 0, 1, 0.01).name('pulse min');
    folder.add(params, 'opacityPulseMax', 0, 1, 0.01).name('pulse max');
    folder.add(params, 'thicknessMin', 0.1, 10, 0.1).name('thickness min');
    folder.add(params, 'thicknessMax', 0.1, 10, 0.1).name('thickness max');
  }
  const secF = gui.addFolder('Holo Laser');
  addSeqLaserGUI(secF, secondLaserParams, { v: false });
  const thirdF = gui.addFolder('Proj Laser');
  addSeqLaserGUI(thirdF, thirdLaserParams, { v: false });

  const camF = gui.addFolder('Camera');
  camF.add(cameraConfig, 'type', ['perspective', 'orthographic']).name('type').onChange(withSave(v => switchCamera(v)));
  camF.add(cameraConfig, 'orthoZoom', 0.5, 40, 0.1).name('ortho zoom').onChange(withSave(v => {
    const w = window.innerWidth, h = window.innerHeight;
    orthoCam.left = -w / v; orthoCam.right = w / v;
    orthoCam.top = h / v; orthoCam.bottom = -h / v;
    orthoCam.updateProjectionMatrix();
  }));
  camF.add(renderer, 'toneMappingExposure', 0.01, 3, 0.01).name('exposure');
  camF.add(flyConfig, 'enabled').name('fly enabled');
  camF.add(flyConfig, 'duration', 0.3, 5, 0.1).name('fly duration');
  const rendF = camF.addFolder('Renderer');
  rendF.add(renderer, 'autoClear').name('auto clear');
  rendF.add(renderer, 'autoClearColor').name('clear color');
  rendF.add(renderer, 'autoClearDepth').name('clear depth');
  rendF.add(renderer, 'autoClearStencil').name('clear stencil');
  // Per-pose camera offsets — editing the current pose updates camera live
  function applyCurrentPose() {
    flyAnim = null; // cancel any active fly — user is manually editing
    const resolved = resolvePose(currentPose);
    camera.position.set(resolved.x, resolved.y, resolved.z);
    camera.rotation.set(resolved.rx, resolved.ry, resolved.rz);
    if (camera.isPerspectiveCamera) { camera.fov = resolved.fov; camera.updateProjectionMatrix(); }
    if (camera.isOrthographicCamera) {
      cameraConfig.orthoZoom = resolved.oz;
      const w = window.innerWidth, h = window.innerHeight;
      camera.left = -w / resolved.oz; camera.right = w / resolved.oz;
      camera.top = h / resolved.oz; camera.bottom = -h / resolved.oz;
      camera.updateProjectionMatrix();
    }
  }
  for (const [name, pose] of Object.entries(camPoses)) {
    const pf = camF.addFolder(name + ' (offset)');
    const poseName = name; // capture for closure
    const liveUpdate = withSave(() => { if (currentPose === poseName) applyCurrentPose(); });
    pf.add(pose, 'x', -2000, 2000, 1).name('x').onChange(liveUpdate);
    pf.add(pose, 'y', -2000, 2000, 1).name('y').onChange(liveUpdate);
    pf.add(pose, 'z', -500, 2000, 1).name('z').onChange(liveUpdate);
    pf.add(pose, 'rx', -Math.PI, Math.PI, 0.01).name('rot x').onChange(liveUpdate);
    pf.add(pose, 'ry', -Math.PI, Math.PI, 0.01).name('rot y').onChange(liveUpdate);
    pf.add(pose, 'rz', -Math.PI, Math.PI, 0.01).name('rot z').onChange(liveUpdate);
    pf.add(pose, 'fov', 20, 120, 1).name('fov').onChange(liveUpdate);
    pf.add(pose, 'oz', 0.0001, 40, 0.01).name('ortho zoom').onChange(liveUpdate);
    pf.add({ go: () => flyTo(name) }, 'go').name('fly here');
  }


  window._hpBuildFnGui = buildFnGui;
  } // end if (gui) — everything below is always initialized

  // Restore saved functions (must be outside gui block so they run on prod)
  if (saved?.lfos) {
    for (const lfoData of saved.lfos) {
      const lfo = createLFO(lfoData);
      lfos.push(lfo);
    }
    // Initialize _smoothed for mouse/random LFOs so first output matches saved state.
    // Generic: call lfoValue at t=0 which uses _smoothed=0.5. The output formula is:
    // output = min + (max - min) * v * strength, where v comes from _smoothed after smoothing.
    // We want the SAVED value (whatever's in the target right now). So we reverse:
    // _smoothed = (savedValue - min) / ((max - min) * strength)
    // To get savedValue generically, we read it via lfoTargets[name].get() if available.
    requestAnimationFrame(() => {
      for (const lfo of lfos) {
        if (!lfo.enabled) continue;
        if (lfo.waveform !== 'mouseX' && lfo.waveform !== 'mouseY' && lfo.waveform !== 'random') continue;
        const tgt = lfoTargets[lfo.target];
        if (!tgt?.get) continue;
        const currentVal = tgt.get();
        const range = (lfo.max - lfo.min) * (lfo.strength || 1);
        if (range === 0) continue;
        lfo._smoothed = Math.max(0, Math.min(1, (currentVal - lfo.min) / range));
      }
    });
    if (gui && window._hpBuildFnGui) {
      for (const lfo of lfos) window._hpBuildFnGui(lfo);
      updateLfoDisabledState();
    }
  }

  const pairings = {
    'Press Start 2P + VT323': { heading: 'Press Start 2P', body: 'VT323', mono: 'VT323' },
    'Syne + Outfit': { heading: 'Syne', body: 'Outfit', mono: 'JetBrains Mono' },
    'Roboto + Lora': { heading: 'Roboto', body: 'Lora', mono: 'Roboto Mono' },
    'Playfair Display + Lato': { heading: 'Playfair Display', body: 'Lato', mono: 'JetBrains Mono' },
    'Montserrat + Merriweather': { heading: 'Montserrat', body: 'Merriweather', mono: 'Fira Code' },
    'Oswald + Source Sans 3': { heading: 'Oswald', body: 'Source Sans 3', mono: 'Source Code Pro' },
    'Raleway + Merriweather': { heading: 'Raleway', body: 'Merriweather', mono: 'JetBrains Mono' },
    'Abril Fatface + Lato': { heading: 'Abril Fatface', body: 'Lato', mono: 'IBM Plex Mono' },
    'DM Serif Display + DM Sans': { heading: 'DM Serif Display', body: 'DM Sans', mono: 'IBM Plex Mono' },
    'Poppins + Lora': { heading: 'Poppins', body: 'Lora', mono: 'JetBrains Mono' },
    'Open Sans + Lora': { heading: 'Open Sans', body: 'Lora', mono: 'Fira Code' },
    'Bebas Neue + Montserrat': { heading: 'Bebas Neue', body: 'Montserrat', mono: 'Space Mono' },
    'Cormorant Garamond + Proza Libre': { heading: 'Cormorant Garamond', body: 'Proza Libre', mono: 'JetBrains Mono' },
    'Space Grotesk + Space Mono': { heading: 'Space Grotesk', body: 'Space Mono', mono: 'Space Mono' },
    'Josefin Sans + Josefin Slab': { heading: 'Josefin Sans', body: 'Josefin Slab', mono: 'Fira Code' },
    'IBM Plex Sans + IBM Plex Serif': { heading: 'IBM Plex Sans', body: 'IBM Plex Serif', mono: 'IBM Plex Mono' },
    'Outfit + Playfair Display': { heading: 'Outfit', body: 'Playfair Display', mono: 'JetBrains Mono' },
    'Manrope + Lora': { heading: 'Manrope', body: 'Lora', mono: 'JetBrains Mono' },
    'Work Sans + Bitter': { heading: 'Work Sans', body: 'Bitter', mono: 'Fira Code' },
    'Nunito + Lora': { heading: 'Nunito', body: 'Lora', mono: 'Fira Code' },
    'PT Sans + PT Serif': { heading: 'PT Sans', body: 'PT Serif', mono: 'PT Mono' },
    'Karla + Inconsolata': { heading: 'Karla', body: 'Inconsolata', mono: 'Inconsolata' },
    'Orbitron + Rajdhani': { heading: 'Orbitron', body: 'Rajdhani', mono: 'Share Tech Mono' },
    'Chakra Petch + Exo 2': { heading: 'Chakra Petch', body: 'Exo 2', mono: 'Fira Code' },
    'Oxanium + Titillium Web': { heading: 'Oxanium', body: 'Titillium Web', mono: 'JetBrains Mono' },
    'Roboto Slab + Roboto': { heading: 'Roboto Slab', body: 'Roboto', mono: 'Roboto Mono' },
    'Unbounded + Figtree': { heading: 'Unbounded', body: 'Figtree', mono: 'JetBrains Mono' },
    'Bricolage Grotesque + DM Sans': { heading: 'Bricolage Grotesque', body: 'DM Sans', mono: 'IBM Plex Mono' },
  };

  function loadGoogleFont(name) {
    const id = 'gf-' + name.replace(/\s/g, '-');
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id; link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}:wght@300;400;500;600;700;800&display=swap`;
    document.head.appendChild(link);
  }

  const fontParams = { heading: 'Karla', body: 'Inconsolata', mono: 'Inconsolata', brand: 'Orbitron', brandWeight: '700', brandKerning: 0.15, brandSize: 1.1, pairing: 'Karla + Inconsolata', ...(DEFAULTS.fonts || {}) };
  if (saved?.fonts) { Object.assign(fontParams, saved.fonts); }
  function applyFonts() {
    loadGoogleFont(fontParams.heading); loadGoogleFont(fontParams.body); loadGoogleFont(fontParams.mono); loadGoogleFont(fontParams.brand);
    document.documentElement.style.setProperty('--font-heading', `'${fontParams.heading}', sans-serif`);
    document.documentElement.style.setProperty('--font-body', `'${fontParams.body}', sans-serif`);
    document.documentElement.style.setProperty('--font-mono', `'${fontParams.mono}', monospace`);
    document.documentElement.style.setProperty('--font-brand', `'${fontParams.brand}', sans-serif`);
    document.documentElement.style.setProperty('--font-brand-weight', fontParams.brandWeight);
    document.documentElement.style.setProperty('--font-brand-kerning', fontParams.brandKerning + 'em');
    document.documentElement.style.setProperty('--font-brand-size', fontParams.brandSize + 'rem');
  }
  applyFonts();
  // Fade in navbar items after fonts load
  document.fonts.ready.then(() => {
    document.body.classList.add('fonts-loaded');
  });

  if (gui) {
  const allFonts = ['Abril Fatface', 'Archivo Black', 'Audiowide', 'Bebas Neue', 'Bitter', 'Bricolage Grotesque', 'Bungee', 'Chakra Petch', 'Commissioner', 'Cormorant Garamond', 'Courier Prime', 'DM Sans', 'DM Serif Display', 'Electrolize', 'Exo 2', 'Figtree', 'Fira Code', 'Fraunces', 'IBM Plex Mono', 'IBM Plex Sans', 'IBM Plex Serif', 'Inconsolata', 'Inter Tight', 'JetBrains Mono', 'Josefin Sans', 'Josefin Slab', 'Karla', 'Lato', 'Lora', 'Manrope', 'Merriweather', 'Michroma', 'Montserrat', 'Nunito', 'Open Sans', 'Orbitron', 'Oswald', 'Outfit', 'Oxanium', 'Playfair Display', 'Poppins', 'Press Start 2P', 'Proza Libre', 'PT Mono', 'PT Sans', 'PT Serif', 'Rajdhani', 'Raleway', 'Righteous', 'Roboto', 'Roboto Mono', 'Roboto Slab', 'Share Tech Mono', 'Source Code Pro', 'Source Sans 3', 'Space Grotesk', 'Space Mono', 'Syne', 'Titillium Web', 'Unbounded', 'VT323', 'Work Sans'];
  const displayFonts = allFonts;
  const bodyFonts = allFonts;
  const monoFonts = allFonts;
  const font3dFonts = allFonts;

  const fontF = gui.addFolder('Fonts');
  fontF.add(fontParams, 'pairing', Object.keys(pairings)).name('Pairing').onChange(withSave(v => {
    const p = pairings[v]; fontParams.heading = p.heading; fontParams.body = p.body; fontParams.mono = p.mono;
    applyFonts(); fontF.controllersRecursive().forEach(c => c.updateDisplay());
  }));
  fontF.add(fontParams, 'heading', displayFonts).name('Heading').onChange(withSave(() => applyFonts()));
  fontF.add(fontParams, 'body', bodyFonts).name('Body').onChange(withSave(() => applyFonts()));
  fontF.add(fontParams, 'mono', monoFonts).name('Mono').onChange(withSave(() => applyFonts()));
  fontF.add(fontParams, 'brand', [...new Set([...displayFonts, 'Orbitron', 'Audiowide', 'Michroma', 'Rajdhani', 'Chakra Petch', 'Oxanium'])].sort()).name('Brand').onChange(withSave(() => applyFonts()));
  fontF.add(fontParams, 'brandWeight', ['100', '200', '300', '400', '500', '600', '700', '800', '900']).name('Brand Weight').onChange(withSave(() => applyFonts()));
  fontF.add(fontParams, 'brandKerning', 0, 0.5, 0.01).name('Brand Kerning').onChange(withSave(() => applyFonts()));
  fontF.add(fontParams, 'brandSize', 0.5, 3, 0.05).name('Brand Size').onChange(withSave(() => applyFonts()));
  fontParams.font3d = currentFont3D;
  fontF.add(fontParams, 'font3d', font3dFonts).name('3D Text').onChange(withSave(v => change3DFont(v)));

  gui.add({ copy() {
    saveGui(); // populate _lastSaveState
    navigator.clipboard.writeText(JSON.stringify(saveGui._lastState, null, 2));
  }}, 'copy').name('Copy to clipboard');
  gui.add({ save() {
    saveGui(); // populate _lastSaveState
    fetch('/api/save-defaults', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(saveGui._lastState) })
      .then(() => { window._hpDirty = false; })
      .catch(() => {});
  }}, 'save').name('Save to file');

  // Sort GUI folders alphabetically
  if (gui) {
    const container = gui.$children;
    if (container) {
      const folders = [...container.children].filter(el => el.classList.contains('lil-gui'));
      const nonFolders = [...container.children].filter(el => !el.classList.contains('lil-gui'));
      folders.sort((a, b) => {
        const aTitle = a.querySelector(':scope > .title')?.textContent || '';
        const bTitle = b.querySelector(':scope > .title')?.textContent || '';
        return aTitle.localeCompare(bTitle, undefined, { sensitivity: 'base' });
      });
      for (const f of folders) container.appendChild(f);
      for (const nf of nonFolders) container.appendChild(nf);
    }
  }

  // Restore collapse state from localStorage
  let collapseState;
  try { collapseState = JSON.parse(localStorage.getItem('hp_gui_collapsed')); } catch (_) {}
  collapseState = collapseState || saved?.collapsed;
  if (collapseState) {
    restoreCollapsed(gui, collapseState);
    if (collapseState._root) gui.close();
  }
  gui.onOpenClose(() => {
    const state = { '_root': gui._closed };
    for (const folder of gui.foldersRecursive()) state[folder._title] = folder._closed;
    localStorage.setItem('hp_gui_collapsed', JSON.stringify(state));
  });
  for (const f of gui.foldersRecursive()) {
    f.onOpenClose(() => {
      // Save collapse state to localStorage only (not dirty, no beforeunload)
      const state = { '_root': gui._closed };
      for (const folder of gui.foldersRecursive()) state[folder._title] = folder._closed;
      localStorage.setItem('hp_gui_collapsed', JSON.stringify(state));
    });
  }
  } // end if (gui)

  // Events
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('mousemove', onMouse, { passive: true });
  window.addEventListener('touchmove', e => {
    if (e.touches.length) onMouse({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
  }, { passive: true });
  window.addEventListener('touchstart', e => {
    if (e.touches.length) onMouse({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
  }, { passive: true });

  // Accelerometer → mouseRawX/Y on mobile (with permission request on iOS)
  let _accelRequested = false;
  function initAccelerometer() {
    window.addEventListener('deviceorientation', e => {
      // gamma: left-right tilt → mouseRawX (±15° = full range)
      // beta: front-back tilt → mouseRawY (30°-60° = full range, phone held at ~45°)
      if (e.gamma != null) mouseRawX = Math.max(0, Math.min(1, (e.gamma + 15) / 30));
      if (e.beta != null) mouseRawY = Math.max(0, Math.min(1, (e.beta - 30) / 30));
    }, { passive: true });
  }
  if ('ontouchstart' in window) {
    const requestAccel = () => {
      if (_accelRequested) return;
      _accelRequested = true;
      if (typeof DeviceOrientationEvent?.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission().then(state => {
          if (state === 'granted') initAccelerometer();
        }).catch(() => {});
      } else {
        initAccelerometer();
      }
    };
    window.addEventListener('touchstart', requestAccel, { once: true });
  }

  // Register LFO targets
  lfoTargets.holoOpacity = {
    set: (_, v) => {
      wordOpacity.holo = v;
      holoWorldRot.children.forEach(m => {
        if (m.isMesh && m.material) { m.material.opacity = v; if (!m.material.transparent) m.material.transparent = true; }
      });
    },
  };
  lfoTargets.projOpacity = {
    set: (_, v) => {
      wordOpacity.proj = v;
      projWorldRot.children.forEach(m => {
        if (m.isMesh && m.material) { m.material.opacity = v; if (!m.material.transparent) m.material.transparent = true; }
      });
    },
  };
  // Word position/scale/rotation targets
  lfoTargets.holoPosX = { set: (_, v) => { holoWorldPos.x = v; } };
  lfoTargets.holoPosY = { set: (_, v) => { holoWorldPos.y = v; } };
  lfoTargets.holoPosZ = { set: (_, v) => { holoWorldPos.z = v; } };
  lfoTargets.holoScaleX = { set: (_, v) => { holoGroup.scale.x = v; } };
  lfoTargets.holoScaleY = { set: (_, v) => { holoGroup.scale.y = v; } };
  lfoTargets.holoScaleZ = { set: (_, v) => { holoGroup.scale.z = v; } };
  lfoTargets.holoRotX = { set: (_, v) => { holoGroup.rotation.x = v; } };
  lfoTargets.holoRotY = { set: (_, v) => { holoGroup.rotation.y = v; } };
  lfoTargets.holoRotZ = { set: (_, v) => { holoGroup.rotation.z = v; } };
  lfoTargets.projPosX = { set: (_, v) => { projWorldPos.x = v; } };
  lfoTargets.projPosY = { set: (_, v) => { projWorldPos.y = v; } };
  lfoTargets.projPosZ = { set: (_, v) => { projWorldPos.z = v; } };
  lfoTargets.projScaleX = { set: (_, v) => { projGroup.scale.x = v; } };
  lfoTargets.projScaleY = { set: (_, v) => { projGroup.scale.y = v; } };
  lfoTargets.projScaleZ = { set: (_, v) => { projGroup.scale.z = v; } };
  lfoTargets.projRotX = { set: (_, v) => { projGroup.rotation.x = v; } };
  lfoTargets.projRotY = { set: (_, v) => { projGroup.rotation.y = v; } };
  lfoTargets.projRotZ = { set: (_, v) => { projGroup.rotation.z = v; } };
  lfoTargets.textPosX = { set: (_, v) => { textGroup.position.x = v; } };
  lfoTargets.textPosY = { set: (_, v) => { textGroup.position.y = v; } };
  lfoTargets.textPosZ = { set: (_, v) => { textGroup.position.z = v; } };
  lfoTargets.textRotX = { set: (_, v) => { textGroup.rotation.x = v; } };
  lfoTargets.textRotY = { set: (_, v) => { textGroup.rotation.y = v; } };
  lfoTargets.textRotZ = { set: (_, v) => { textGroup.rotation.z = v; } };
  // Instance visibility: 0-1 controls what fraction of instances are visible
  // Stores base opacities at build time, then multiplies by 0 or 1
  function setInstVisibility(wordGroup, fraction) {
    for (const im of instancedMeshes) {
      if (im._wordGroup !== wordGroup) continue;
      const attr = im.geometry.getAttribute('instanceOpacity');
      if (!attr) continue;
      if (!im._baseOpacities) im._baseOpacities = Float32Array.from(attr.array);
      const count = im.count;
      const visible = Math.round(fraction * (count - 1));
      for (let i = 0; i < count; i++) {
        attr.array[i] = (i === 0 || i <= visible) ? im._baseOpacities[i] : 0;
      }
      attr.needsUpdate = true;
    }
  }
  lfoTargets.holoInstVisibility = { set: (_, v) => setInstVisibility(textGroup.children[0], Math.max(0, Math.min(1, v))) };
  lfoTargets.projInstVisibility = { set: (_, v) => setInstVisibility(textGroup.children[1], Math.max(0, Math.min(1, v))) };
  // Instance spread/opacity targets — update matrices+opacities live
  // Instance spread/opacity targets — only rebuild after all letters are ready
  let _instRebuildTimer = null;
  function _throttledInstRebuild() {
    if (_instRebuildTimer) return;
    const delay = window.innerWidth < (mobileParams?.breakpoint || 768) ? 200 : 50;
    _instRebuildTimer = setTimeout(() => {
      _instRebuildTimer = null;
      if (textGroup.userData._instancesBuilt) {
        rebuildInstances(textGroup.children[0], instanceParams.holo);
        rebuildInstances(textGroup.children[1], instanceParams.proj);
      }
    }, delay);
  }
  function holoInstUpdate() { _throttledInstRebuild(); }
  function projInstUpdate() { _throttledInstRebuild(); }
  lfoTargets.holoInstSpreadX = { set: (_, v) => { instanceParams.holo.posSpread.x = v; holoInstUpdate(); } };
  lfoTargets.holoInstSpreadY = { set: (_, v) => { instanceParams.holo.posSpread.y = v; holoInstUpdate(); } };
  lfoTargets.holoInstSpreadZ = { set: (_, v) => { instanceParams.holo.posSpread.z = v; holoInstUpdate(); } };
  lfoTargets.holoInstRotSpreadX = { set: (_, v) => { instanceParams.holo.rotSpread.x = v; holoInstUpdate(); } };
  lfoTargets.holoInstRotSpreadY = { set: (_, v) => { instanceParams.holo.rotSpread.y = v; holoInstUpdate(); } };
  lfoTargets.holoInstRotSpreadZ = { set: (_, v) => { instanceParams.holo.rotSpread.z = v; holoInstUpdate(); } };
  lfoTargets.holoInstOpacityMin = { set: (_, v) => { instanceParams.holo.opacityMin = v; holoInstUpdate(); } };
  lfoTargets.holoInstOpacityMax = { set: (_, v) => { instanceParams.holo.opacityMax = v; holoInstUpdate(); } };
  lfoTargets.projInstSpreadX = { set: (_, v) => { instanceParams.proj.posSpread.x = v; projInstUpdate(); } };
  lfoTargets.projInstSpreadY = { set: (_, v) => { instanceParams.proj.posSpread.y = v; projInstUpdate(); } };
  lfoTargets.projInstSpreadZ = { set: (_, v) => { instanceParams.proj.posSpread.z = v; projInstUpdate(); } };
  lfoTargets.projInstRotSpreadX = { set: (_, v) => { instanceParams.proj.rotSpread.x = v; projInstUpdate(); } };
  lfoTargets.projInstRotSpreadY = { set: (_, v) => { instanceParams.proj.rotSpread.y = v; projInstUpdate(); } };
  lfoTargets.projInstRotSpreadZ = { set: (_, v) => { instanceParams.proj.rotSpread.z = v; projInstUpdate(); } };
  lfoTargets.projInstOpacityMin = { set: (_, v) => { instanceParams.proj.opacityMin = v; projInstUpdate(); } };
  lfoTargets.projInstOpacityMax = { set: (_, v) => { instanceParams.proj.opacityMax = v; projInstUpdate(); } };
  lfoTargets.timeScale = { set: (_, v) => { timeParams.scale = v; } };
  lfoTargets.laserTimeScale = { set: (_, v) => { timeParams.laserScale = v; } };
  lfoTargets.textTimeScale = { set: (_, v) => { timeParams.textScale = v; } };
  lfoTargets.helixTwist = { set: (_, v) => { helixTwist = v; } };
  lfoTargets.shadowLightIntensity = { set: (_, v) => { dirLight.intensity = v; } };
  lfoTargets.shadowLightX = { set: (_, v) => { dirLight.position.x = v; } };
  lfoTargets.shadowLightY = { set: (_, v) => { dirLight.position.y = v; } };
  lfoTargets.shadowLightZ = { set: (_, v) => { dirLight.position.z = v; } };
  lfoTargets.camPosX = { set: (_, v) => { camera.position.x = v; } };
  lfoTargets.camPosY = { set: (_, v) => { camera.position.y = v; } };
  lfoTargets.camPosZ = { set: (_, v) => { camera.position.z = v; } };
  lfoTargets.camRotX = { set: (_, v) => { camera.rotation.x = v; } };
  lfoTargets.camRotY = { set: (_, v) => { camera.rotation.y = v; } };
  lfoTargets.camRotZ = { set: (_, v) => { camera.rotation.z = v; } };
  lfoTargets.noiseSpeed = { set: (_, v) => { noiseParams.speed = v; updateAllMats(m => { if (m.userData.shader) m.userData.shader.uniforms.uNoiseSpeed.value = v; }); } };
  lfoTargets.noiseScale = { set: (_, v) => { noiseParams.scale = v; updateAllMats(m => { if (m.userData.shader) m.userData.shader.uniforms.uNoiseScale.value = v; }); } };
  lfoTargets.noiseIntensity = { set: (_, v) => { noiseParams.intensity = v; updateAllMats(m => { if (m.userData.shader) m.userData.shader.uniforms.uNoiseIntensity.value = v; }); } };
  lfoTargets.laserGroupX = { set: (_, v) => { laserGroup.position.x = v; } };
  lfoTargets.laserGroupY = { set: (_, v) => { laserGroup.position.y = v; } };
  lfoTargets.laserGroupZ = { set: (_, v) => { laserGroup.position.z = v; } };
  lfoTargets.fogDensity = {
    set: (_, v) => { if (scene.fog) { scene.fog.density = v; scene.fog.userData.baseDensity = v; } },
  };
  let _accentDirty = false;
  function flushAccent() {
    if (_accentDirty && window._hpApplyAccent) {
      window._hpApplyAccent();
      _accentDirty = false;
    }
  }
  setInterval(flushAccent, 100);
  lfoTargets.accentHue = {
    set: (_, v) => { if (!_mouseHasMoved) return; if (window._hpAccentParams && Math.abs(window._hpAccentParams.hue - v) > 0.1) { window._hpAccentParams.hue = v; _accentDirty = true; } },
    get: () => window._hpAccentParams?.hue ?? 0,
  };
  lfoTargets.accentSat = {
    set: (_, v) => { if (!_mouseHasMoved) return; if (window._hpAccentParams && Math.abs(window._hpAccentParams.saturation - v) > 0.1) { window._hpAccentParams.saturation = v; _accentDirty = true; } },
    get: () => window._hpAccentParams?.saturation ?? 50,
  };

  // rebuildInstances is at module scope
  // Start render loop
  let _renderPaused = false;
  window._hpPauseRender = (v) => { _renderPaused = v; if (!v) requestAnimationFrame(loop); };
  // Render one frame without advancing time — used on theme toggle to flush new fog color
  // to the canvas even when the loop is paused on mobile overlays.
  window._hpFlushRender = () => { if (composer) composer.render(); };
  function loop() {
    if (!_renderPaused) render();
    if (!_renderPaused) requestAnimationFrame(loop);
  }
  requestAnimationFrame(loop);
}

function onMouse(e) {
  if (!_mouseHasMoved) {
    _mouseHasMoved = true;
    const now = performance.now() / 1000;
    for (const lfo of lfos) lfo._rampStart = now;
  }
  const nx = (e.clientX / window.innerWidth) * 2 - 1;
  const ny = (e.clientY / window.innerHeight) * 2 - 1;
  targetRotX = -ny * 0.08;
  targetRotY = nx * 0.04;
  mouseRawX = e.clientX / window.innerWidth;
  mouseRawY = e.clientY / window.innerHeight;
}

function onResize() {
  const w = window.innerWidth, h = window.innerHeight;
  if (camera.isPerspectiveCamera) {
    camera.aspect = w / h;
  } else {
    const z = cameraConfig.orthoZoom;
    camera.left = -w / z; camera.right = w / z;
    camera.top = h / z; camera.bottom = -h / z;
  }
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
  if (composer) composer.setSize(w, h);
  applyMobileOverrides();
}

function render() {
  const realT = performance.now() * 0.001;
  const dt = realT - (lastRealTime || realT);
  lastRealTime = realT;
  scaledTime += dt * timeParams.scale;
  laserTime += dt * timeParams.laserScale;
  textTime += dt * timeParams.textScale;
  const t = scaledTime;
  timeUniform.value = textTime; // shader noise uses text time


  // Function updates
  function applyToTarget(targetName, val) {
    const tgt = lfoTargets[targetName];
    if (tgt && tgt.set) { tgt.set(null, val); return; }
    if (targetName.startsWith('fn:')) {
      const resolved = resolveFnTarget(targetName);
      if (resolved && lfos[resolved.idx]) {
        const p = resolved.param;
        if (p === 'waveform') {
          const wi = Math.min(Math.floor(val * WAVEFORMS.length), WAVEFORMS.length - 1);
          lfos[resolved.idx].waveform = WAVEFORMS[wi];
        } else {
          lfos[resolved.idx][p] = val;
        }
      }
    }
  }
  // LFO blend-in: s-curve from 0→1 after letters finish building
  const _lfoBlendDuration = animParams.lfoBlendTime ?? 3000;
  let lfoBlend = 1;
  if (textGroup?.userData?._lfoBlendStart) {
    const elapsed = (performance.now() - textGroup.userData._lfoBlendStart) / _lfoBlendDuration;
    if (elapsed >= 1) {
      textGroup.userData._lfoBlendStart = null; // done blending
    } else {
      const s = Math.max(0, Math.min(1, elapsed));
      lfoBlend = s * s * (3 - 2 * s); // smoothstep
    }
  } else if (!textGroup?.userData?._instancesBuilt) {
    lfoBlend = 0; // letters still loading — no LFO effect
  }

  // Sort LFOs by dependency (DAG) so modulating functions run before their targets
  if (!render._sortedLfos || render._sortedLfos.length !== lfos.length) {
    // Build dependency: if lfo A targets fn:B:param, A must run BEFORE B (A feeds into B)
    // So B depends on A. Topological sort: sources first, sinks last.
    const nameToIdx = new Map();
    lfos.forEach((l, i) => nameToIdx.set(l.name, i));
    const deps = lfos.map(() => []); // deps[i] = indices that must run before i
    for (let i = 0; i < lfos.length; i++) {
      const allTargets = [lfos[i].target, ...(lfos[i].targets || [])];
      for (const tgt of allTargets) {
        if (!tgt.startsWith('fn:')) continue;
        const resolved = resolveFnTarget(tgt);
        if (resolved && resolved.idx !== i) deps[resolved.idx].push(i); // i must run before resolved.idx
      }
    }
    // Kahn's algorithm
    const inDeg = lfos.map((_, i) => deps.reduce((c, d) => c + d.filter(x => x === i).length, 0));
    const queue = [];
    for (let i = 0; i < lfos.length; i++) if (inDeg[i] === 0) queue.push(i);
    const sorted = [];
    while (queue.length) {
      const i = queue.shift();
      sorted.push(i);
      for (const j of deps[i]) { inDeg[j]--; if (inDeg[j] === 0) queue.push(j); }
    }
    // If cycle, just append missing indices
    if (sorted.length < lfos.length) {
      for (let i = 0; i < lfos.length; i++) if (!sorted.includes(i)) sorted.push(i);
    }
    render._sortedLfos = sorted;
  }

  // On mobile, run LFOs every 3rd frame to save CPU
  const _isMobile = window.innerWidth < (mobileParams?.breakpoint || 768);
  if (lfoBlend > 0 && (!_isMobile || (render._frameCount = (render._frameCount || 0) + 1) % 3 === 0))
  for (const li of render._sortedLfos) {
    const lfo = lfos[li];
    if (!fnGlobal.enabled || !lfo.enabled) continue;
    const raw = lfoValue(lfo, t);
    // Blend from current resting state toward LFO output
    if (lfoBlend < 1 && lfo._restVal === undefined) lfo._restVal = raw; // capture first LFO value as rest
    const val = lfoBlend >= 1 ? raw : (lfo._restVal ?? raw) + (raw - (lfo._restVal ?? raw)) * lfoBlend;
    applyToTarget(lfo.target, val);
    if (lfo.targets) for (const t2 of lfo.targets) applyToTarget(t2, val);
  }

  // Camera fly animation
  if (flyAnim) {
    const elapsed = performance.now() - flyAnim.startTime;
    const raw = Math.min(elapsed / flyAnim.duration, 1);
    const e = proEase(raw);
    const { from, to } = flyAnim;
    const lerp = (a, b) => a + (b - a) * e;
    camera.position.x = lerp(from.x, to.x);
    camera.position.y = lerp(from.y, to.y);
    camera.position.z = lerp(from.z, to.z);
    camera.rotation.x = lerp(from.rx, to.rx);
    camera.rotation.y = lerp(from.ry, to.ry);
    camera.rotation.z = lerp(from.rz, to.rz);
    if (camera.isPerspectiveCamera) {
      camera.fov = lerp(from.fov, to.fov);
      camera.updateProjectionMatrix();
    }
    if (camera.isOrthographicCamera) {
      cameraConfig.orthoZoom = lerp(from.oz, to.oz);
      const ow = window.innerWidth, oh = window.innerHeight;
      camera.left = -ow / cameraConfig.orthoZoom; camera.right = ow / cameraConfig.orthoZoom;
      camera.top = oh / cameraConfig.orthoZoom; camera.bottom = -oh / cameraConfig.orthoZoom;
      camera.updateProjectionMatrix();
    }
    if (raw >= 1) flyAnim = null;
  }

  // Camera light follows camera
  if (camLight) camLight.position.copy(camera.position);

  if (textGroup) {
    // textGroup rotation is set by GUI controls only (no mouse override)

    // Helix rotation — each letter rotates on Y with a phase offset
    const hx = textGroup.userData.helix;
    if (hx) {
      const speed = hx.getSpeed();
      const phase = hx.getPhase();
      const twist = hx.getTwist();
      for (const wordGroup of textGroup.children) {
        const wrot = wordGroup.children[0];
        if (!wrot) continue;
        let li = 0;
        for (const child of wrot.children) {
          if (child.isInstancedMesh) {
            child.rotation.y = Math.sin(textTime * speed) * Math.PI * 0.5 + MAGIC_SMUDGE_ANGLE;
            continue;
          }
          child.rotation.y = Math.sin(textTime * speed + li * phase * Math.PI) * Math.PI * 0.5 + li * twist;
          li++;
        }
      }
    }

    // Laser beams: connect each HOLOGRAPHIC letter to its PROJECTIONS pair
    const hg = textGroup.children[0]?.children[0]; // holoGroup → holoWorldRot (letters)
    const pg = textGroup.children[1]?.children[0]; // projGroup → projWorldRot (letters)
    const hgLetters = hg ? hg.children.filter(c => c.isMesh && !c.isInstancedMesh) : [];
    const pgLetters = pg ? pg.children.filter(c => c.isMesh && !c.isInstancedMesh) : [];
    if (hg && pg && hgLetters.length === 11 && pgLetters.length === 11) {
      // Build instances once ALL letters for BOTH words have finished fading
      if (!textGroup.userData._instancesBuilt && fadingLetters.length === 0 && !textGroup.userData._buildQueue?.length) {
        textGroup.userData._instancesBuilt = true;
        textGroup.userData._lfoBlendStart = performance.now();
        if (window._hpAnimateBranding) window._hpAnimateBranding();
        if (instanceParams.holo.count > 1) rebuildInstances(textGroup.children[0], instanceParams.holo);
        if (instanceParams.proj.count > 1) rebuildInstances(textGroup.children[1], instanceParams.proj);
        // Sync instanced mesh colors immediately
        for (const im of instancedMeshes) {
          if (!im.material || !im._wordGroup) continue;
          im.material.color.copy(im._wordGroup === textGroup.children[0] ? primaryColor3D : tertiaryColor3D);
        }
      }
      // ── Second laser source (→HOLO) ──
      if (!secondBuilt || secondBeams.length !== secondLaserParams.poolSize) {
        for (const l of secondBeams) { laserGroup.remove(l.group); l.group.traverse(c => { if (c.geometry) c.geometry.dispose(); if (c.material) c.material.dispose(); }); }
        secondBeams.length = 0;
        for (let i = 0; i < secondLaserParams.poolSize; i++) {
          const coreGeo = new THREE.CylinderGeometry(0.06, 0.06, 1, 4, 1, true);
          const coreMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
          const core = new THREE.Mesh(coreGeo, coreMat);
          const glowGeo = new THREE.CylinderGeometry(0.3, 0.3, 1, 4, 1, true);
          const glowMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, side: THREE.DoubleSide });
          const glow = new THREE.Mesh(glowGeo, glowMat);
          const haloGeo = new THREE.CylinderGeometry(0.8, 0.8, 1, 4, 1, true);
          const haloMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, side: THREE.DoubleSide });
          const halo = new THREE.Mesh(haloGeo, haloMat);
          const group = new THREE.Group();
          group.add(core); group.add(glow); group.add(halo);
          group.visible = false;
          laserGroup.add(group);
          secondBeams.push({ group, coreMat, glowMat, haloMat, active: false, birthTime: 0, lifespan: 0, opacityFactor: 1, src: new THREE.Vector3(), dst: new THREE.Vector3() });
        }
        secondBuilt = true;
      }

      // Sequential laser helper — fires one beam per tick through all vertices of a target group
      // Sequential laser — advances one vertex at a time, waits for beam to finish before advancing
      function spawnSeqBeam(params, beams, targetGroup) {
        if (!params.enabled) return;
        if (!targetGroup || !targetGroup.children.length) return;
        if (!params._seq || typeof params._seq.lastFireTime !== 'number') {
          params._seq = { meshIdx: 0, vertIdx: 0, lastFireTime: -999, currentLife: 0 };
        }
        // Reset stale timing from saved state (lastFireTime from a previous session)
        if (params._seq.lastFireTime > laserTime) params._seq.lastFireTime = -999;
        const seq = params._seq;

        // Wait until the previous beam's lifespan has elapsed before advancing
        if (laserTime - seq.lastFireTime < seq.currentLife) return;

        const beam = beams.find(be => !be.active);
        if (!beam) return;

        const mesh = targetGroup.children[seq.meshIdx % targetGroup.children.length];
        if (!mesh?.geometry) return;

        const pos = mesh.geometry.attributes.position;
        const jitterMax = params.targetsMax || 0;
        const jitterOffset = jitterMax > 0 ? Math.floor((Math.random() * 2 - 1) * jitterMax) : 0;
        const vIdx = ((seq.vertIdx + jitterOffset) % pos.count + pos.count) % pos.count; // wrap safely
        const v = new THREE.Vector3(pos.getX(vIdx), pos.getY(vIdx), pos.getZ(vIdx));
        mesh.localToWorld(v);

        // Advance to next vertex — step oscillates via sine wave
        const sineT = (Math.sin(laserTime * params.stepFreq * Math.PI * 2 + params.stepPhase) + 1) / 2;
        const step = Math.max(1, Math.round(params.stepMin + (params.stepMax - params.stepMin) * sineT));
        seq.vertIdx += step;
        if (seq.vertIdx >= pos.count) {
          seq.vertIdx = 0;
          seq.meshIdx = (seq.meshIdx + 1) % targetGroup.children.length;
        }

        const lifespan = params.boltLifeMin + Math.random() * (params.boltLifeMax - params.boltLifeMin);
        seq.lastFireTime = laserTime;
        seq.currentLife = lifespan * params.spawnRate; // spawnRate scales the wait (1 = wait full life, 0.5 = wait half)

        const srcPoint = new THREE.Vector3(params.originX, params.originY, params.originZ);
        const color = getLaserColor(params.colorSource);
        beam.src.copy(srcPoint);
        beam.dst.copy(v);
        beam.birthTime = laserTime;
        beam.lifespan = lifespan;
        beam.opacityFactor = params.opacityMin + Math.random() * (params.opacityMax - params.opacityMin);
        beam.active = true;
        beam.group.visible = false;
        params._latestBeam = beam;
        beam.thickness = params.thicknessMin + Math.random() * (params.thicknessMax - params.thicknessMin);
        beam.glowMat.color.copy(color);
        beam.haloMat.color.copy(color);
      }

      // Second laser → HOLOGRAPHIC
      spawnSeqBeam(secondLaserParams, secondBeams, hg);

      // Compute holo laser strength from latest beam
      if (secondLaserParams._latestBeam?.active) {
        const lb = secondLaserParams._latestBeam;
        const age = laserTime - lb.birthTime;
        const life = age / lb.lifespan;
        holoLaserStrength = life < 0.15 ? life / 0.15 : Math.max(0, 1 - ((life - 0.15) / 0.85));
      } else { holoLaserStrength = 0; }
      // Update second source beams (holo laser)
      for (const beam of secondBeams) {
        if (!beam.active) continue;
        if (laserTime < beam.birthTime) continue;
        beam.group.visible = true;

        const age = laserTime - beam.birthTime;
        const life = age / beam.lifespan;
        if (life > 1) {
          beam.active = false;
          beam.group.visible = false;
          continue;
        }

        const intensity = life < 0.15 ? life / 0.15 : 1 - ((life - 0.15) / 0.85);
        const growT = Math.min(life * 3, 1);
        const currentEnd = new THREE.Vector3().lerpVectors(beam.src, beam.dst, growT);
        const mid = new THREE.Vector3().addVectors(beam.src, currentEnd).multiplyScalar(0.5);
        const dist = beam.src.distanceTo(currentEnd);
        beam.group.position.copy(mid);
        beam.group.lookAt(currentEnd);
        beam.group.rotateX(Math.PI / 2);
        const thick = beam.thickness || 1;
        beam.group.scale.set(thick, Math.max(dist, 0.01), thick);

        const pulse = secondLaserParams.opacityPulseFreq > 0
          ? secondLaserParams.opacityPulseMin + (secondLaserParams.opacityPulseMax - secondLaserParams.opacityPulseMin) * ((Math.sin(laserTime * secondLaserParams.opacityPulseFreq * Math.PI * 2) + 1) / 2)
          : 1;
        const o = beam.opacityFactor * pulse;
        beam.coreMat.opacity = 0.9 * intensity * o;
        beam.glowMat.opacity = 0.3 * intensity * o;
        beam.haloMat.opacity = 0.1 * intensity * o;
      }

      // ── Third laser source → PROJECTIONS ──
      if (!thirdBuilt || thirdBeams.length !== thirdLaserParams.poolSize) {
        for (const l of thirdBeams) { laserGroup.remove(l.group); l.group.traverse(c => { if (c.geometry) c.geometry.dispose(); if (c.material) c.material.dispose(); }); }
        thirdBeams.length = 0;
        for (let i = 0; i < thirdLaserParams.poolSize; i++) {
          const coreGeo = new THREE.CylinderGeometry(0.06, 0.06, 1, 4, 1, true);
          const coreMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
          const core = new THREE.Mesh(coreGeo, coreMat);
          const glowGeo = new THREE.CylinderGeometry(0.3, 0.3, 1, 4, 1, true);
          const glowMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, side: THREE.DoubleSide });
          const glow = new THREE.Mesh(glowGeo, glowMat);
          const haloGeo = new THREE.CylinderGeometry(0.8, 0.8, 1, 4, 1, true);
          const haloMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, side: THREE.DoubleSide });
          const halo = new THREE.Mesh(haloGeo, haloMat);
          const group = new THREE.Group();
          group.add(core); group.add(glow); group.add(halo);
          group.visible = false;
          laserGroup.add(group);
          thirdBeams.push({ group, coreMat, glowMat, haloMat, active: false, birthTime: 0, lifespan: 0, opacityFactor: 1, src: new THREE.Vector3(), dst: new THREE.Vector3() });
        }
        thirdBuilt = true;
      }
      // Compute proj laser strength from latest beam
      if (thirdLaserParams._latestBeam?.active) {
        const lb = thirdLaserParams._latestBeam;
        const age = laserTime - lb.birthTime;
        const life = age / lb.lifespan;
        projLaserStrength = life < 0.15 ? life / 0.15 : Math.max(0, 1 - ((life - 0.15) / 0.85));
      } else { projLaserStrength = 0; }
      spawnSeqBeam(thirdLaserParams, thirdBeams, pg);
      for (const beam of thirdBeams) {
        if (!beam.active) continue;
        if (laserTime < beam.birthTime) continue;
        beam.group.visible = true;
        const age = laserTime - beam.birthTime;
        const life = age / beam.lifespan;
        if (life > 1) { beam.active = false; beam.group.visible = false; continue; }
        const intensity = life < 0.15 ? life / 0.15 : 1 - ((life - 0.15) / 0.85);
        const growT = Math.min(life * 3, 1);
        const currentEnd = new THREE.Vector3().lerpVectors(beam.src, beam.dst, growT);
        const mid = new THREE.Vector3().addVectors(beam.src, currentEnd).multiplyScalar(0.5);
        const dist = beam.src.distanceTo(currentEnd);
        beam.group.position.copy(mid);
        beam.group.lookAt(currentEnd);
        beam.group.rotateX(Math.PI / 2);
        const thick = beam.thickness || 1;
        beam.group.scale.set(thick, Math.max(dist, 0.01), thick);
        const pulse3 = thirdLaserParams.opacityPulseFreq > 0
          ? thirdLaserParams.opacityPulseMin + (thirdLaserParams.opacityPulseMax - thirdLaserParams.opacityPulseMin) * ((Math.sin(laserTime * thirdLaserParams.opacityPulseFreq * Math.PI * 2) + 1) / 2)
          : 1;
        const o = beam.opacityFactor * pulse3;
        beam.coreMat.opacity = 0.9 * intensity * o;
        beam.glowMat.opacity = 0.3 * intensity * o;
        beam.haloMat.opacity = 0.1 * intensity * o;
      }
    }

  }

  // Build letters from queue with configurable interval
  if (material && textGroup?.userData?.buildQueue?.length > 0) {
    const now2 = performance.now();
    const queue = textGroup.userData.buildQueue;
    const counts = textGroup.userData.buildCounts;
    const lastBuild = textGroup.userData.lastBuildTime || 0;

    if (now2 - lastBuild >= animParams.letterInterval) {
      textGroup.userData.lastBuildTime = now2;
      const item = queue.shift();
      const wordLen = item.word.length;
      const spacing = item.word === 'HOLOGRAPHIC' ? textGroup.userData.holoSpacing : textGroup.userData.projSpacing;
      const totalH = spacing * (wordLen - 1);
      const idx = item.posIdx !== undefined ? item.posIdx : counts[item.word];
      counts[item.word]++;

      const geo = new TextGeometry(item.ch, item.params);
      geo.center();
      const lerpT = idx / (wordLen - 1 || 1);
      // HOLOGRAPHIC = primary hue, PROJECTIONS = secondary hue
      const baseColor = item.word === 'HOLOGRAPHIC' ? primaryColor3D : tertiaryColor3D;
      const endColor = new THREE.Color().copy(baseColor).offsetHSL(0.05, 0, -0.1);
      const letterColor = new THREE.Color().copy(baseColor).lerp(endColor, lerpT);
      const letterMat = new THREE.MeshPhysicalMaterial({
        color: letterColor,
        metalness: material.metalness,
        roughness: material.roughness,
        transmission: material.transmission,
        thickness: material.thickness,
        ior: material.ior,
        transparent: true,
        opacity: 0,
      });
      applyNoiseShader(letterMat);
      const mesh = new THREE.Mesh(geo, letterMat);
      mesh.castShadow = true;
      mesh.position.y = totalH / 2 - idx * spacing;
      item.group.add(mesh);
      fadingLetters.push({ mesh, startTime: performance.now(), isHolo: item.word === 'HOLOGRAPHIC' });
    }
  }

  // Fade in letters
  const now = performance.now();
  for (let i = fadingLetters.length - 1; i >= 0; i--) {
    const fl = fadingLetters[i];
    const elapsed = now - fl.startTime;
    if (elapsed > 0) {
      // Use live wordOpacity (tracks LFO) so there's no pop when fade ends
      const target = fl.isHolo ? wordOpacity.holo : wordOpacity.proj;
      const ft = animParams.fadeTime || 600;
      fl.mesh.material.opacity = Math.min(elapsed / ft, 1) * target;
      if (elapsed / ft >= 1) {
        fl.mesh.material.transparent = true; // keep transparent for LFO opacity control
        fadingLetters.splice(i, 1);
      }
    }
  }

  // Debug overlay — dev only
  if (isDev) {
    if (!window._hpDebug) {
      const d = document.createElement('div');
      d.style.cssText = 'position:fixed;bottom:50px;right:0;background:rgba(0,0,0,0.7);color:#0f0;font:11px monospace;padding:6px 10px;z-index:10002;pointer-events:none;white-space:pre;';
      document.body.appendChild(d);
      window._hpDebug = d;
    }
    window._hpDebug.textContent =
      `pose: ${currentPose}${flyAnim ? ' (flying)' : ''}\n` +
      `cam: ${camera.position.x.toFixed(0)} ${camera.position.y.toFixed(0)} ${camera.position.z.toFixed(0)}\n` +
      `rot: ${camera.rotation.x.toFixed(2)} ${camera.rotation.y.toFixed(2)} ${camera.rotation.z.toFixed(2)}\n` +
      `mouse: ${mouseRawX.toFixed(3)} ${mouseRawY.toFixed(3)}`;
  }

  // Staggered shader compilation — one mesh per frame
  if (_compileQueue.length > 0) {
    const im = _compileQueue.shift();
    const showIm = () => {
      const isHolo = im._wordGroup === textGroup?.children[0];
      if (im.material) im.material.color.copy(isHolo ? primaryColor3D : tertiaryColor3D);
      if (im._pendingLetters) { im._pendingLetters.forEach(m => { m.visible = false; }); im._pendingLetters = null; }
      im.visible = true;
    };
    if (renderer.compileAsync) {
      renderer.compileAsync(im, camera, scene).then(showIm);
    } else {
      showIm();
    }
  }

  // Branding glow oscillation
  if (window._hpLogoColors) {
    const glow = 6 + Math.sin(realT * 0.2) * 4; // 2-10px pulse, slow breathe
    const spread = 14 + Math.sin(realT * 0.2) * 8; // 6-22px pulse, slow breathe
    const { h: hc, p: pc } = window._hpLogoColors;
    const hShadow = `0 0 ${glow}px ${hc}, 0 0 ${spread}px ${hc}40`;
    const pShadow = `0 0 ${glow}px ${pc}, 0 0 ${spread}px ${pc}40`;
    document.querySelectorAll('.logo-h').forEach(el => { el.style.textShadow = hShadow; });
    document.querySelectorAll('.logo-p').forEach(el => { el.style.textShadow = pShadow; });
  }

  if (stats) stats.update();
  if (composer) composer.render();
  else renderer.render(scene, camera);
}

// ───────────────────────────────────────────
// APP LAUNCH
// ───────────────────────────────────────────
function launchApp() {
  initNav();
  initReveals();
  initThree();

  window.addEventListener('beforeunload', e => {
    if (window._hpDirty) { e.preventDefault(); e.returnValue = ''; }
  });

  $('#theme-toggle').addEventListener('click', () => {
    const next = themeSetting === 'dark' ? 'light' : themeSetting === 'light' ? 'auto' : 'dark';
    applyTheme(next);
  });
  $('#lang-toggle').addEventListener('click', () => applyLang(lang === 'en' ? 'nl' : 'en'));
  $('#contact-form').addEventListener('submit', handleSubmit);
  // Mobile menu
  const mobileMenu = $('#mobile-menu');
  $('#menu-toggle')?.addEventListener('click', () => mobileMenu.classList.toggle('open'));
  mobileMenu?.querySelectorAll('a').forEach(a => {
    a.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      mobileMenu.classList.remove('open');
      const href = a.getAttribute('href');
      const target = navFlyTargets[href];
      if (target) {
        flyTo(target);
        history.replaceState(null, '', href);
      }
    });
  });

  $('#turnstile-close').addEventListener('click', () => {
    $('#turnstile-overlay').classList.remove('active');
    pendingForm = null;
  });
  // Close overlay on backdrop click
  $('#turnstile-overlay').addEventListener('click', e => {
    if (e.target === e.currentTarget) {
      e.currentTarget.classList.remove('active');
      pendingForm = null;
    }
  });

}

// ───────────────────────────────────────────
// INIT
// ───────────────────────────────────────────
// Populate header branding letters (hidden initially, animated after 3D text loads)
(function() {
  const words = [
    { el: document.querySelector('.logo-h.logo-letters'), text: 'HOLOGRAPHIC' },
    { el: document.querySelector('.logo-p.logo-letters'), text: 'PROJECTIONS' },
  ];
  for (const w of words) {
    if (!w.el) continue;
    for (const ch of w.text) {
      const span = document.createElement('span');
      span.textContent = ch;
      span.style.opacity = '0';
      span.style.transition = 'opacity 0.3s';
      span.className = 'brand-letter';
      w.el.appendChild(span);
    }
  }
})();

window._hpAnimateBranding = function() {
  const letters = document.querySelectorAll('.brand-letter');
  if (currentPose !== 'home') {
    letters.forEach(el => { el.style.opacity = '1'; });
    return;
  }
  setTimeout(() => {
    letters.forEach(el => { el.style.opacity = '1'; });
  }, 1000);
};

applyTheme(theme);
applyLang(lang);
launchApp();
